import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getCurrentUser, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Icon } from '@iconify/react';
import SEO from '../components/SEO';
import './WorkflowDetail.css';

const WorkflowDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showProcessDetail, setShowProcessDetail] = useState(false);
  const [showGuideDetail, setShowGuideDetail] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null: 확인 중, true: 로그인, false: 비로그인
  const [enlargedImage, setEnlargedImage] = useState(null); // 확대된 이미지 상태
  const [searchCount, setSearchCount] = useState(0); // 오늘 검색 횟수
  const [searchLimitReached, setSearchLimitReached] = useState(false); // 검색 횟수 제한 도달 여부
  
  // 백엔드 API URL (환경 변수 또는 기본값)
  // eslint-disable-next-line no-unused-vars
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  
  // 검색 횟수 제한 상수
  const MAX_SEARCHES_PER_DAY = 3;
  
  // 로컬 스토리지 키
  const SEARCH_COUNT_KEY = 'ai_search_count';
  const SEARCH_DATE_KEY = 'ai_search_date';

  // 시뮬레이션된 워크플로우 데이터
  const workflowData = {
    1: {
      title: '스타트업 비즈니스 플랜 자동 생성',
      category: '창업',
      description: 'AI가 시장 분석부터 재무 계획까지 완벽한 비즈니스 플랜을 자동으로 생성합니다. 스타트업 창업자들이 빠르고 정확한 비즈니스 플랜을 작성할 수 있도록 도와주는 혁신적인 솔루션입니다.',
      price: 99000,
      originalPrice: 149000,
      rating: 4.9,
      reviews: 156,
      icon: 'mdi:file-chart-outline',
      iconColor: 'linear-gradient(135deg, #007BFF 0%, #0062CC 100%)',
      features: [
        '시장 분석 및 경쟁사 조사',
        'SWOT 분석 자동 생성',
        '재무 계획 및 예산 수립',
        '마케팅 전략 수립',
        '리스크 분석 및 대응 방안',
        '투자 유치 계획서 작성'
      ],
      benefits: [
        '비즈니스 플랜 작성 시간 80% 단축',
        '전문가 수준의 분석 보고서 생성',
        '투자 유치 성공률 3배 향상',
        '시장 진입 전략 최적화'
      ],
      requirements: [
        '기본적인 비즈니스 아이디어',
        '목표 시장 정보',
        '예상 자본금 규모',
        '팀 구성 정보'
      ],
      steps: [
        {
          step: 1,
          title: '정보 입력',
          description: '비즈니스 아이디어와 기본 정보를 입력합니다.',
          duration: '5분'
        },
        {
          step: 2,
          title: '시장 분석',
          description: 'AI가 시장 동향과 경쟁사를 분석합니다.',
          duration: '10분'
        },
        {
          step: 3,
          title: '재무 계획',
          description: '수익 모델과 재무 계획을 자동 생성합니다.',
          duration: '15분'
        },
        {
          step: 4,
          title: '최종 보고서',
          description: '완성된 비즈니스 플랜을 다운로드합니다.',
          duration: '5분'
        }
      ],
      testimonials: [
        {
          name: '김창업',
          company: '테크스타트업 대표',
          content: '정말 놀라운 도구입니다. 3일 걸리던 비즈니스 플랜을 30분 만에 완성했어요!',
          rating: 5
        },
        {
          name: '이혁신',
          company: '스타트업 창업자',
          content: '투자 유치에 성공했습니다. 전문가 수준의 분석이 정말 도움이 되었어요.',
          rating: 5
        }
      ]
    },
    3: {
      title: '경쟁사 서비스 공략 포인트 분석',
      category: '분석',
      description: '입력 쿼리 기반의 AI 분석 자동화. 경쟁 서비스의 비즈니스 인사이트를 자동으로 추출합니다. 경쟁사 서비스의 기능, 가격, 마케팅 전략 등을 종합적으로 분석하여 시장 경쟁력을 강화할 수 있는 인사이트를 제공합니다.',
      price: 199000,
      originalPrice: 299000,
      rating: 4.9,
      reviews: 234,
      icon: 'mdi:crosshairs-gps',
      iconColor: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
      features: [
        '경쟁사 서비스 정보 수집',
        'AI 기반 분석 및 비교',
        '비즈니스 인사이트 추출',
        '시장 포지셔닝 분석',
        '가격 전략 분석',
        '기능 비교 리포트 생성'
      ],
      benefits: [
        '경쟁사 분석 시간 90% 단축',
        '데이터 기반 의사결정 지원',
        '시장 기회 발굴 및 활용',
        '경쟁 우위 확보 전략 수립'
      ],
      requirements: [
        '분석할 경쟁사 서비스 정보',
        '비교 기준 및 관심 영역',
        '분석 목적 및 활용 방안',
        'AI 분석 쿼리 입력'
      ],
      steps: [
        {
          step: 1,
          title: 'Webhook 트리거',
          description: 'POST 요청을 통해 외부에서 경쟁사명(query) 값을 받아 워크플로우를 시작합니다.',
          duration: '즉시'
        },
        {
          step: 2,
          title: '입력 처리',
          description: 'Webhook에서 받은 body.query 값을 queries 변수로 설정하여 워크플로우 내에서 사용하기 쉽게 준비합니다.',
          duration: '즉시'
        },
        {
          step: 3,
          title: 'AI 분석 (Gemini Flash)',
          description: 'Gemini Flash 모델이 빠른 3줄 요약 리포트를 생성하여 Webhook에 즉시 응답합니다.',
          duration: '10-30초'
        },
        {
          step: 4,
          title: 'AI 분석 (Gemini Pro)',
          description: 'Gemini Pro 모델이 상세한 1페이지 분석 리포트를 생성합니다.',
          duration: '30-60초'
        },
        {
          step: 5,
          title: 'Webhook 즉시 응답',
          description: 'Flash 모델이 생성한 3줄 요약 리포트를 Webhook을 호출한 사용자에게 즉시 반환합니다.',
          duration: '즉시'
        },
        {
          step: 6,
          title: 'Airtable 상세 저장',
          description: 'Pro 모델이 생성한 상세 분석 리포트를 Airtable에 저장합니다.',
          duration: '즉시'
        }
      ],
      testimonials: [
        {
          name: '김분석',
          company: '전략 기획팀장',
          content: '경쟁사 분석이 이렇게 쉬워질 줄 몰랐어요. AI가 핵심 인사이트를 정확하게 추출해줍니다!',
          rating: 5
        },
        {
          name: '이경쟁',
          company: '제품 개발자',
          content: '경쟁사 서비스의 강점과 약점을 한눈에 파악할 수 있어서 제품 전략 수립에 큰 도움이 됩니다.',
          rating: 5
        }
      ]
    },
    4: {
      title: '정부과제 신규 공지 자동 알람',
      category: '정부과제',
      description: '자신의 사업 영역에 맞는 정부 과제가 신규로 등록되면 자동으로 알림을 보내주는 자동화 워크플로우',
      price: 299000,
      originalPrice: 399000,
      rating: 4.7,
      reviews: 67,
      icon: 'mdi:bell-badge-outline',
      iconColor: 'linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)',
      features: [
        '정부과제 모니터링',
        '자동 알림',
        '사업 영역 필터링',
        '실시간 공지 확인',
        '다중 기관 지원',
        'Airtable 연동'
      ],
      benefits: [
        '정부과제 신규 공지 자동 모니터링으로 놓치는 기회 방지',
        '사업 영역에 맞는 과제만 선별하여 알림',
        '3개 기관(창업진흥원, 중소벤처기업부, 과기정통부) 통합 관리',
        'Airtable을 통한 체계적인 데이터 관리'
      ],
      requirements: [
        '공공데이터포털 Service Key',
        'Airtable 계정 및 Base 설정',
        'n8n 최신 버전',
        'HTTP Request 및 Code 노드 활용 가능'
      ],
      steps: [
        {
          step: 1,
          title: 'API 호출 및 데이터 수집',
          description: '3개 기관(창업진흥원, 중소벤처기업부, 과학기술정보통신부)의 API를 병렬로 호출하여 최신 공고 정보를 수집합니다.',
          duration: '5분'
        },
        {
          step: 2,
          title: '데이터 변환 및 전처리',
          description: 'XML 형식의 응답을 JSON으로 변환하고, 각 기관별로 다른 데이터 구조를 통일된 형식으로 표준화합니다.',
          duration: '3분'
        },
        {
          step: 3,
          title: '데이터 표준화',
          description: '서로 다른 필드명을 Airtable 스키마에 맞춰 통일하고, 필수 정보(공고명, 공고일, 공고링크 등)를 추출합니다.',
          duration: '2분'
        },
        {
          step: 4,
          title: 'Airtable 저장',
          description: '표준화된 데이터를 Airtable에 Upsert 방식으로 저장하여 중복을 방지하고 최신 정보를 유지합니다.',
          duration: '1분'
        }
      ],
      testimonials: [
        {
          name: '박정부',
          company: '스타트업 대표',
          content: '정부과제를 놓치지 않고 빠르게 신청할 수 있어서 정말 유용합니다. 자동화 덕분에 시간을 많이 절약했어요!',
          rating: 5
        },
        {
          name: '이사업',
          company: '중소기업 경영자',
          content: '3개 기관의 공고를 한 곳에서 관리할 수 있어서 정말 편리합니다. Airtable 연동도 완벽해요.',
          rating: 5
        }
      ]
    },
    5: {
      title: 'n8n 크롤링 및 블로그 작성 자동화',
      category: '마케팅',
      description: 'n8n을 활용하여 웹 크롤링부터 블로그 작성까지 완전 자동화합니다. 최신 뉴스나 콘텐츠를 자동으로 수집하고, AI가 분석하여 블로그 포스트를 생성한 후 자동으로 게시하는 혁신적인 솔루션입니다.',
      price: 179000,
      originalPrice: 299000,
      rating: 4.8,
      reviews: 123,
      icon: 'mdi:bullhorn-outline',
      iconColor: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
      features: [
        '웹 크롤링 자동화',
        'AI 기반 콘텐츠 분석',
        '블로그 포스트 자동 생성',
        '자동 게시 및 스케줄링',
        'SEO 최적화 콘텐츠 생성',
        '다양한 블로그 플랫폼 지원'
      ],
      benefits: [
        '블로그 작성 시간 95% 단축',
        '최신 트렌드 자동 반영',
        'SEO 최적화된 콘텐츠 자동 생성',
        '콘텐츠 마케팅 효율성 극대화'
      ],
      requirements: [
        '크롤링할 웹사이트 URL 또는 키워드',
        '블로그 플랫폼 연동 정보',
        '콘텐츠 주제 및 스타일 설정',
        '게시 스케줄 설정'
      ],
      steps: [
        {
          step: 1,
          title: '스케줄 실행 및 웹 크롤링',
          description: '매일 오전 10시에 자동으로 실행되며, 설정한 웹사이트나 API 엔드포인트에서 최신 콘텐츠를 크롤링합니다.',
          duration: '5분'
        },
        {
          step: 2,
          title: '데이터 전처리 및 집계',
          description: '수집된 데이터를 표준화(title, content, date)하고, 블로그 작성에 적합한 형태로 전처리한 후 집계합니다.',
          duration: '3분'
        },
        {
          step: 3,
          title: 'AI Agent 블로그 포스트 생성',
          description: 'Airtable에서 로드한 시스템 프롬프트와 집계된 콘텐츠를 Google Gemini 2.5 Pro 모델에 전달하여 SEO 최적화된 블로그 포스트를 JSON 형식으로 생성합니다.',
          duration: '10분'
        },
        {
          step: 4,
          title: 'JSON 파싱 및 이미지 생성',
          description: 'AI 응답에서 JSON을 추출하고 파싱합니다. 선택적으로 Gemini 2.5 Flash 모델을 사용하여 이미지 프롬프트 기반 썸네일 이미지를 생성합니다.',
          duration: '5분'
        },
        {
          step: 5,
          title: '블로그 플랫폼 자동 게시',
          description: '생성된 블로그 포스트와 이미지를 WordPress, Medium, Tistory 등 설정한 블로그 플랫폼의 API를 통해 자동으로 게시합니다.',
          duration: '2분'
        }
      ],
      testimonials: [
        {
          name: '김블로거',
          company: '콘텐츠 마케터',
          content: '매일 블로그 포스트를 작성하는 것이 이렇게 쉬워질 줄 몰랐어요. 크롤링부터 게시까지 완전 자동화되어 정말 편리합니다!',
          rating: 5
        },
        {
          name: '이마케팅',
          company: '디지털 마케팅 전문가',
          content: 'SEO 최적화된 콘텐츠가 자동으로 생성되어 검색 엔진 노출이 크게 향상되었습니다.',
          rating: 5
        }
      ]
    },
    7: {
      title: '최신 뉴스 크롤링(무료)',
      category: '분석',
      description: '원하는 분야의 최신 뉴스를 크롤링해서 홈페이지에 표시합니다. 실시간으로 업데이트되는 뉴스 정보를 자동으로 수집하고 정리하여 사용자에게 제공합니다.',
      price: 2000,
      originalPrice: 5000,
      rating: 4.3,
      reviews: 20,
      icon: 'mdi:rss',
      iconColor: 'linear-gradient(135deg, #00BFA5 0%, #009688 100%)',
      features: [
        '트렌드 분석',
        '뉴스 크롤링',
        '자동 분류 및 정리',
        '실시간 업데이트',
        '다양한 뉴스 소스 지원',
        '키워드 필터링'
      ],
      benefits: [
        '최신 뉴스 자동 수집으로 업무 시간 90% 절약',
        '관심 분야 뉴스만 선별하여 제공',
        '홈페이지에 실시간 뉴스 섹션 구축',
        '트렌드 분석으로 시장 동향 파악'
      ],
      requirements: [
        '크롤링할 뉴스 분야 선택',
        '키워드 설정',
        '업데이트 주기 설정',
        '홈페이지 연동 정보'
      ],
      steps: [
        {
          step: 1,
          title: '설정 입력',
          description: '크롤링할 뉴스 분야와 키워드를 설정합니다.',
          duration: '10초'
        },
        {
          step: 2,
          title: '뉴스 크롤링 시작',
          description: '설정한 조건에 맞는 최신 뉴스를 자동으로 수집합니다.',
          duration: '1분'
        },
        {
          step: 3,
          title: 'n8n 워크플로우 실행',
          description: 'n8n 워크플로우를 실행하여 수집된 뉴스를 분류하여 데이터 저장합니다.',
          duration: '1분'
        },
        {
          step: 4,
          title: '홈페이지 표시',
          description: '정리된 뉴스를 홈페이지에 자동으로 업데이트합니다.',
          duration: '1분'
        }
      ],
      testimonials: [
        {
          name: '박뉴스',
          company: '마케팅 전문가',
          content: '실시간으로 업데이트되는 뉴스가 정말 유용합니다. 트렌드를 빠르게 파악할 수 있어요!',
          rating: 5
        },
        {
          name: '최정보',
          company: '콘텐츠 제작자',
          content: '홈페이지에 뉴스 섹션이 자동으로 생기니 사용자들이 자주 방문하게 되었어요.',
          rating: 4
        }
      ]
    },
    8: {
      title: '최신 유튜브 영상 분석 및 비즈니스 활용',
      category: '분석',
      description: '최신 유튜브를 크롤링 하고 영상 스크립트를 분석하여 최신 기술 가이드 및 비즈니스 활용 아이디어 도출',
      price: 249000,
      originalPrice: 349000,
      rating: 4.7,
      reviews: 89,
      icon: 'mdi:youtube',
      iconColor: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)',
      features: [
        '유튜브 크롤링',
        '영상 스크립트 분석',
        '기술 가이드 생성',
        '비즈니스 아이디어 도출'
      ],
      benefits: [
        '해외 기술 트렌드 자동 모니터링',
        '학습 가이드 자동 생성으로 학습 효율 향상',
        '비즈니스 활용 방안 도출로 기회 발굴',
        '지식 아카이빙 자동화'
      ],
      requirements: [
        '모니터링할 YouTube 채널 RSS 피드',
        'Python 환경 및 youtube-transcript-api 라이브러리',
        'Google Gemini API 키',
        'YouTube Data API 키',
        'Airtable 계정 및 Base 설정'
      ],
      steps: [
        {
          step: 1,
          title: 'RSS 피드 모니터링',
          description: '지정된 YouTube 채널의 RSS 피드를 매시간 확인하여 신규 영상을 감지합니다.',
          duration: '즉시'
        },
        {
          step: 2,
          title: '영상 필터링',
          description: 'Shorts 영상 및 라이브 스트리밍 영상을 제외하고 정규 영상만 선별합니다.',
          duration: '즉시'
        },
        {
          step: 3,
          title: '스크립트 추출',
          description: 'Python 스크립트를 실행하여 해당 영상의 자막(Transcript)을 추출합니다.',
          duration: '10-30초'
        },
        {
          step: 4,
          title: 'AI 분석',
          description: 'Google Gemini 모델을 통해 스크립트를 분석하여 한줄 요약, 상세 학습 가이드, 비즈니스 활용 방안을 도출합니다.',
          duration: '30-60초'
        },
        {
          step: 5,
          title: '데이터 저장',
          description: '분석된 데이터를 Airtable 데이터베이스에 구조화하여 저장합니다.',
          duration: '즉시'
        }
      ],
      testimonials: [
        {
          name: '김유튜브',
          company: '기술 트렌드 분석가',
          content: '해외 기술 유튜버들의 최신 영상을 자동으로 분석받을 수 있어서 정말 유용합니다. 학습 가이드와 비즈니스 아이디어까지 자동으로 생성되어 시간을 많이 절약할 수 있어요!',
          rating: 5
        },
        {
          name: '이비즈니스',
          company: '스타트업 창업자',
          content: '최신 기술 트렌드를 빠르게 파악하고 비즈니스에 적용할 수 있는 아이디어를 얻을 수 있어서 정말 도움이 됩니다.',
          rating: 5
        }
      ]
    },
    9: {
      title: '100만 달러 수익을 위한 3단계 AI 비즈니스 구축 가이드',
      category: '창업',
      description: 'AI를 활용한 수익성 높은 비즈니스 모델을 단계별로 구축하는 실전 가이드입니다. 학습(Learn) → 실행(Do) → 교육(Teach)의 검증된 모델을 따라 100만 달러 수익을 달성할 수 있는 체계적인 방법을 제시합니다.',
      price: 349000,
      originalPrice: 499000,
      rating: 4.9,
      reviews: 156,
      icon: 'mdi:currency-usd-circle-outline',
      iconColor: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      features: [
        'AI 기술 습득 및 전문성 확보',
        '서비스 판매 및 고객 확보 전략',
        '지식 상품화 및 스케일업 방법',
        '검증된 3단계 비즈니스 모델',
        '실전 도구 활용 가이드'
      ],
      benefits: [
        '검증된 Learn-Do-Teach 모델로 체계적인 비즈니스 구축',
        'AI 도구 활용으로 빠른 전문성 확보 및 서비스 개발',
        '자동화된 영업 및 마케팅 프로세스로 효율성 극대화',
        '교육 상품화를 통한 수익 다각화 및 스케일업',
        '실전 사례와 실행 과제를 통한 즉시 적용 가능한 가이드'
      ],
      requirements: [
        'AI 도구에 대한 기본 이해',
        '비즈니스 구축 의지와 실행력',
        '초기 투자 가능한 시간과 자원',
        '고객과의 소통 능력',
        '지속적인 학습과 개선 의지'
      ],
      steps: [
        {
          step: 1,
          title: 'Phase 1: 학습 단계 (Learn the Thing) - 핵심 기술 습득',
          description: '세 가지 AI 서비스(교육, 컨설팅, 개발) 중 하나를 선택하여 시장에서 가치를 제공할 수 있는 전문성을 확보합니다. ChatGPT & Claude, Lovable, Retell AI, Make.com 중 하나를 선택해 깊이 파고들어 실전 프로젝트를 완성합니다.',
          duration: '4-8주'
        },
        {
          step: 2,
          title: 'Phase 2: 판매 단계 (Sell the Thing) - 서비스 판매 및 고객 확보',
          description: '학습한 기술을 서비스로 만들어 잠재 고객에게 효과적으로 알리고 판매합니다. Blot AI/Poppy AI로 콘텐츠를 생성하고, Instantly AI로 콜드 이메일 아웃리치를 자동화하며, Gamma로 제안서를 빠르게 제작하여 영업 효율을 극대화합니다.',
          duration: '2-4주'
        },
        {
          step: 3,
          title: 'Phase 3: 확장 단계 (Teach the Thing) - 지식 상품화 및 스케일업',
          description: '에이전시 운영을 통해 축적된 경험과 노하우를 교육 상품(코스, 커뮤니티)으로 만들어 더 큰 규모의 수익을 창출합니다. Skool + Fiducia로 커뮤니티를 구축하고, HeyGen으로 교육 콘텐츠를 효율적으로 제작하여 지속 가능한 수익 모델을 완성합니다.',
          duration: '지속적'
        }
      ],
      testimonials: [
        {
          name: '박성공',
          company: 'AI 컨설팅 대표',
          content: 'Learn-Do-Teach 모델이 정말 체계적이었습니다. Phase 1에서 ChatGPT 전문성을 쌓고, Phase 2에서 첫 고객을 확보했어요. 이제 Phase 3로 교육 상품을 준비 중입니다!',
          rating: 5
        },
        {
          name: '최수익',
          company: 'AI 자동화 에이전시 대표',
          content: 'Make.com으로 시작해서 지금은 월 500만원 이상 수익을 올리고 있습니다. 3단계 모델이 명확해서 따라하기 쉬웠어요.',
          rating: 5
        }
      ]
    },
    10: {
      title: 'Notion 기반 포괄적 개인 노트 관리 시스템 구축',
      category: '생산성',
      description: 'Notion의 빈 페이지에서 시작하여, PARA tagging 시스템, 자동화 템플릿, 웹 클리핑 기능까지 갖춘 포괄적인 개인용 노트 관리 시스템을 구축하는 방법을 단계별로 안내합니다.',
      price: 199000,
      originalPrice: 299000,
      rating: 4.8,
      reviews: 92,
      icon: 'mdi:note-multiple-outline',
      iconColor: 'linear-gradient(135deg, #64748B 0%, #334155 100%)',
      features: [
        'PARA 태깅 시스템 구축',
        'Notes 및 Tags 데이터베이스 생성',
        '자동화 템플릿 구성',
        '웹 클리핑 기능',
        '대시보드 뷰 구성',
        '계층적 태그 구조'
      ],
      benefits: [
        '모든 정보를 체계적으로 관리하고 빠르게 검색 가능',
        'PARA 메소드 기반의 효율적인 정보 분류 시스템',
        '자동화 템플릿으로 반복 작업 시간 절약',
        '웹 클리핑으로 중요한 정보를 한 곳에 모음',
        '대시보드 뷰로 상황에 맞는 정보 즉시 확인',
        '계층적 태그 구조로 유연한 정보 조직화'
      ],
      requirements: [
        'Notion 계정 (무료/유료)',
        '기본적인 Notion 사용 경험',
        '체계적인 정보 관리에 대한 관심',
        'PARA 메소드에 대한 기본 이해 (선택사항)'
      ],
      steps: [
        {
          step: 1,
          title: '기본 데이터베이스 생성',
          description: "'Ultimate Notes' 페이지를 생성하고 'Notes'와 'Tags' 데이터베이스를 설정합니다.",
          duration: '10분'
        },
        {
          step: 2,
          title: "'Notes' 데이터베이스 속성 설정",
          description: 'Favorite, Tags, Type, URL, Note Date 등 필요한 속성을 추가하고 레이아웃을 구성합니다.',
          duration: '15분'
        },
        {
          step: 3,
          title: "'Tags' 데이터베이스 속성 설정",
          description: 'PARA 메소드 기반의 Type 속성과 계층 구조를 위한 관계형 속성을 설정합니다.',
          duration: '15분'
        },
        {
          step: 4,
          title: '대시보드 뷰 구성',
          description: '목적에 맞는 다양한 뷰(All, Inbox, Clips, Journal, Favorites 등)를 생성하고 필터를 설정합니다.',
          duration: '20분'
        },
        {
          step: 5,
          title: '자동화 템플릿 생성',
          description: 'Tags 템플릿과 Journal/Meeting 템플릿을 생성하여 반복 작업을 자동화합니다.',
          duration: '15분'
        }
      ],
      testimonials: [
        {
          name: '김노트',
          company: '프로덕트 매니저',
          content: 'PARA 시스템과 Notion을 결합하니 모든 정보가 체계적으로 정리되기 시작했어요. 웹 클리핑 기능도 정말 유용합니다!',
          rating: 5
        },
        {
          name: '이정리',
          company: '콘텐츠 크리에이터',
          content: '자동화 템플릿 덕분에 저널 작성이 훨씬 쉬워졌어요. 태그 시스템도 계층적으로 구성되어 있어서 정보 찾기가 정말 빠릅니다.',
          rating: 5
        }
      ]
    }
  };

  const workflow = workflowData[id] || workflowData[1];

  // SEO를 위한 구조화된 데이터 생성
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: workflow.title,
    description: workflow.description,
    category: workflow.category,
    offers: {
      '@type': 'Offer',
      price: workflow.price,
      priceCurrency: 'KRW',
      availability: 'https://schema.org/InStock',
      url: `https://flowto-ai.com/workflow/${id}`
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: workflow.rating,
      reviewCount: workflow.reviews
    },
    brand: {
      '@type': 'Brand',
      name: 'FLOWTO.ai'
    }
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '홈',
        item: 'https://flowto-ai.com'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '워크플로우',
        item: 'https://flowto-ai.com/#workflows'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: workflow.title,
        item: `https://flowto-ai.com/workflow/${id}`
      }
    ]
  };

  // 페이지 마운트 시 스크롤을 상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 검색 횟수 초기화 및 확인
  useEffect(() => {
    if (isAuthenticated === true) {
      const today = new Date().toDateString();
      const savedDate = localStorage.getItem(SEARCH_DATE_KEY);
      const savedCount = parseInt(localStorage.getItem(SEARCH_COUNT_KEY) || '0', 10);
      
      // 날짜가 변경되었으면 검색 횟수 리셋
      if (savedDate !== today) {
        localStorage.setItem(SEARCH_DATE_KEY, today);
        localStorage.setItem(SEARCH_COUNT_KEY, '0');
        setSearchCount(0);
        setSearchLimitReached(false);
      } else {
        setSearchCount(savedCount);
        setSearchLimitReached(savedCount >= MAX_SEARCHES_PER_DAY);
      }
    } else {
      setSearchCount(0);
      setSearchLimitReached(false);
    }
  }, [isAuthenticated]);

  // 로그인 상태 확인
  useEffect(() => {
    const checkUser = async () => {
      try {
        await getCurrentUser();
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };

    checkUser();

    // 로그인/로그아웃 이벤트 감지
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          setIsAuthenticated(true);
          break;
        case 'signedOut':
          setIsAuthenticated(false);
          // 로그아웃 시 상세 내용 숨기기 및 검색 횟수 초기화
          if (id === '3' || id === '5') {
            setShowProcessDetail(false);
            setShowGuideDetail(false);
          }
          localStorage.removeItem(SEARCH_COUNT_KEY);
          localStorage.removeItem(SEARCH_DATE_KEY);
          setSearchCount(0);
          setSearchLimitReached(false);
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, [id]);

  // 로그인 상태 변경 시 상세 내용 숨기기 (workflow/3, workflow/5일 때만)
  useEffect(() => {
    if ((id === '3' || id === '5') && isAuthenticated === false) {
      setShowProcessDetail(false);
      setShowGuideDetail(false);
    }
  }, [isAuthenticated, id]);

  // 탭 변경 시 showProcessDetail, showGuideDetail 상태 리셋 (workflow/3, workflow/5일 때만)
  useEffect(() => {
    if (id === '3' || id === '5') {
      setShowProcessDetail(false);
      setShowGuideDetail(false);
    }
  }, [activeTab, id]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  const handleCloseSearchModal = useCallback(() => {
    setShowSearchModal(false);
    setSearchQuery('');
  }, []);

  // ESC 키로 검색 모달 닫기
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSearchModal) {
        handleCloseSearchModal();
      }
    };

    if (showSearchModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showSearchModal, handleCloseSearchModal]);

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    // 로그인 확인
    if (isAuthenticated !== true) {
      setSearchError('로그인이 필요합니다. 로그인 후 검색해주세요.');
      return;
    }
    
    // 검색 횟수 확인
    const today = new Date().toDateString();
    const savedDate = localStorage.getItem(SEARCH_DATE_KEY);
    const savedCount = parseInt(localStorage.getItem(SEARCH_COUNT_KEY) || '0', 10);
    
    // 날짜가 변경되었으면 검색 횟수 리셋
    if (savedDate !== today) {
      localStorage.setItem(SEARCH_DATE_KEY, today);
      localStorage.setItem(SEARCH_COUNT_KEY, '0');
      setSearchCount(0);
      setSearchLimitReached(false);
    }
    
    // 검색 횟수 제한 확인
    if (savedDate === today && savedCount >= MAX_SEARCHES_PER_DAY) {
      setSearchError(`하루 최대 ${MAX_SEARCHES_PER_DAY}회만 검색할 수 있습니다. 내일 다시 시도해주세요.`);
      setSearchLimitReached(true);
      return;
    }
    
    setIsSearching(true);
    setSearchError(null);
    setSearchResult(null);
    
    try {
      const webhookUrl = 'https://bshong-n8n.ngrok.io/webhook/2cfc7a88-a774-4279-9edc-4208b7ce1666'; // Live URL
      
      console.log('🔍 AI 검색 웹훅 호출:', webhookUrl);
      console.log('전송할 데이터:', { query: searchQuery.trim(), timestamp: new Date().toISOString() });
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({
          query: searchQuery.trim(),
          timestamp: new Date().toISOString()
        })
      });
      
      // 응답 본문 읽기 (상태 코드와 관계없이)
      const responseText = await response.text();
      console.log('📄 웹훅 응답 상태:', response.status, response.statusText);
      console.log('📄 웹훅 응답 본문:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('📦 파싱된 JSON 데이터:', data);
      } catch (e) {
        // JSON이 아닌 경우 텍스트로 처리
        console.log('⚠️ JSON 파싱 실패, 텍스트로 처리');
        data = { message: responseText || '검색 요청이 전송되었습니다.' };
      }
      
      // 에러 응답 처리 - 에러도 모달에 표시
      if (!response.ok) {
        const errorMessage = data.message || data.error || `웹훅 호출 실패 (${response.status})`;
        console.error('❌ 웹훅 에러 응답:', errorMessage);
        
        // 에러 응답도 모달에 표시
        setSearchResult({
          answer: `**⚠️ 웹훅 호출 오류**\n\n${errorMessage}\n\n**상태 코드:** ${response.status}\n\n**응답 내용:**\n\`\`\`json\n${JSON.stringify(data, null, 2)}\n\`\`\`\n\n**해결 방법:**\n- n8n 워크플로우가 활성화되어 있는지 확인하세요.\n- 워크플로우에 "Respond to Webhook" 노드가 제대로 연결되어 있는지 확인하세요.\n- 워크플로우 실행 로그를 확인하세요.`,
          citations: [],
          query: searchQuery.trim(),
          isError: true
        });
        setShowSearchModal(true);
        return;
      }
      
      // 성공 응답 처리 - Respond to Webhook 응답 형식 지원
      console.log('📦 응답 데이터 구조:', Object.keys(data));
      
      // 다양한 응답 형식 지원 (Respond to Webhook에서 반환할 수 있는 모든 형식)
      let answer = null;
      let citations = [];
      
      // 1. 직접 응답 필드 확인 (한글 키 "내용" 우선 확인)
      if (data['내용']) {
        // 한글 키 "내용" 필드 우선 처리
        answer = typeof data['내용'] === 'string' ? data['내용'] : JSON.stringify(data['내용'], null, 2);
        console.log('✅ "내용" 필드에서 응답 추출');
      } else if (data.answer) {
        answer = typeof data.answer === 'string' ? data.answer : JSON.stringify(data.answer, null, 2);
      } else if (data.message) {
        answer = typeof data.message === 'string' ? data.message : JSON.stringify(data.message, null, 2);
      } else if (data.response) {
        answer = typeof data.response === 'string' ? data.response : JSON.stringify(data.response, null, 2);
      } else if (data.text) {
        answer = typeof data.text === 'string' ? data.text : JSON.stringify(data.text, null, 2);
      } else if (data.content) {
        answer = typeof data.content === 'string' ? data.content : JSON.stringify(data.content, null, 2);
      } else if (data.result) {
        answer = typeof data.result === 'string' ? data.result : JSON.stringify(data.result, null, 2);
      } else if (data.data) {
        // data.data 형식인 경우
        if (typeof data.data === 'string') {
          answer = data.data;
        } else if (data.data['내용']) {
          // 중첩된 "내용" 필드 확인
          answer = typeof data.data['내용'] === 'string' ? data.data['내용'] : JSON.stringify(data.data['내용'], null, 2);
          console.log('✅ data.data["내용"] 필드에서 응답 추출');
        } else if (data.data.answer || data.data.message || data.data.response) {
          answer = data.data.answer || data.data.message || data.data.response;
        } else {
          answer = JSON.stringify(data.data, null, 2);
        }
      }
      
      // 2. citations/sources/references 추출
      citations = data.citations || data.sources || data.references || data.reference || [];
      if (data.data && (data.data.citations || data.data.sources || data.data.references)) {
        citations = data.data.citations || data.data.sources || data.data.references;
      }
      
      // 3. 응답이 없는 경우 처리
      if (!answer) {
        // 객체의 모든 값을 확인
        const stringValues = Object.values(data).filter(v => typeof v === 'string' && v.length > 0);
        if (stringValues.length > 0) {
          // 첫 번째 문자열 값을 사용
          answer = stringValues[0];
          console.log('⚠️ 명확한 응답 필드가 없어 첫 번째 문자열 값 사용:', answer.substring(0, 100));
        } else {
          // 전체 데이터를 포맷팅하여 표시
          console.warn('⚠️ 명확한 응답 필드가 없음, 전체 데이터 표시');
          answer = `**응답 데이터**\n\n\`\`\`json\n${JSON.stringify(data, null, 2)}\n\`\`\``;
        }
      }
      
      // 4. 검색 결과 설정 및 모달 열기
      setSearchResult({
        answer: answer,
        citations: Array.isArray(citations) ? citations : [],
        query: searchQuery.trim()
      });
      
      // 검색 성공 시 횟수 증가
      const today = new Date().toDateString();
      const savedDate = localStorage.getItem(SEARCH_DATE_KEY);
      const savedCount = parseInt(localStorage.getItem(SEARCH_COUNT_KEY) || '0', 10);
      const newCount = savedDate === today ? savedCount + 1 : 1;
      localStorage.setItem(SEARCH_COUNT_KEY, newCount.toString());
      localStorage.setItem(SEARCH_DATE_KEY, today);
      setSearchCount(newCount);
      setSearchLimitReached(newCount >= MAX_SEARCHES_PER_DAY);
      
      setShowSearchModal(true);
      console.log('✅ 검색 결과 모달 열기');
      console.log('📋 응답 내용 길이:', answer.length, 'characters');
      console.log('📚 참고 자료 개수:', citations.length);
      console.log(`📊 오늘 검색 횟수: ${newCount}/${MAX_SEARCHES_PER_DAY}`);
      
    } catch (error) {
      console.error('❌ Search error:', error);
      console.error('에러 상세:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      setSearchError(error.message || '검색 중 오류가 발생했습니다. 네트워크 연결을 확인해주세요.');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="workflow-detail">
      <SEO
        title={workflow.title}
        description={workflow.description}
        url={`/workflow/${id}`}
        type="product"
        structuredData={[structuredData, breadcrumbStructuredData]}
      />
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">홈</Link>
          <span> / </span>
          <Link to="/#workflows">워크플로우</Link>
          <span> / </span>
          <span>{workflow.title}</span>
        </div>

        <div className="workflow-header">
          <div className="workflow-info">
            <h1 className="workflow-title">
              {workflow.title}
              <span className="workflow-badge">{workflow.category}</span>
            </h1>
            <p className="workflow-description">{workflow.description}</p>
            
            <button className="btn-download-source">
              <Icon icon="mdi:download-outline" width={18} height={18} />
              워크플로우 소스 다운로드
            </button>
          </div>
          
          <div className="workflow-visual">
            <div className="workflow-image" style={{ background: workflow.iconColor }}>
              <Icon icon={workflow.icon} width={64} height={64} color="#ffffff" />
            </div>
            <div className="price-section">
              <div className="price-info">
                <span className="current-price">{formatPrice(workflow.price)}원</span>
                <span className="original-price">{formatPrice(workflow.originalPrice)}원</span>
                <span className="discount">33% 할인</span>
              </div>
              <button className="btn-start">워크플로우 시작하기</button>
            </div>
          </div>
        </div>

        {(id === '4' || id === '5' || id === '7' || id === '8') && (
          <div className="workflow-image-container">
            <img 
              src={
                id === '4' ? "/images/workflow/n8n_government_task.png"
                : id === '5' ? "/images/workflow/n8n_blog_automation.png" 
                : id === '7' ? "/images/workflow/n8n_news_crawling.png"
                : "/images/workflow/n8n_youtube_analysis.png"
              } 
              alt={
                id === '4' ? "n8n 정부과제 신규 공지 자동 알람 워크플로우"
                : id === '5' ? "n8n 크롤링 및 블로그 작성 자동화 워크플로우" 
                : id === '7' ? "n8n 뉴스 크롤링 워크플로우"
                : "n8n 유튜브 영상 분석 및 비즈니스 활용 워크플로우"
              } 
              className="workflow-diagram-image"
              onClick={() => setEnlargedImage({
                src: id === '4' ? "/images/workflow/n8n_government_task.png"
                  : id === '5' ? "/images/workflow/n8n_blog_automation.png" 
                  : id === '7' ? "/images/workflow/n8n_news_crawling.png"
                  : "/images/workflow/n8n_youtube_analysis.png",
                alt: id === '4' ? "n8n 정부과제 신규 공지 자동 알람 워크플로우"
                  : id === '5' ? "n8n 크롤링 및 블로그 작성 자동화 워크플로우" 
                  : id === '7' ? "n8n 뉴스 크롤링 워크플로우"
                  : "n8n 유튜브 영상 분석 및 비즈니스 활용 워크플로우"
              })}
              style={{ cursor: 'pointer' }}
            />
          </div>
        )}

        <div className="workflow-tabs">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            개요
          </button>
          <button 
            className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
            onClick={() => setActiveTab('features')}
          >
            주요 기능
          </button>
          <button 
            className={`tab-btn ${activeTab === 'process' ? 'active' : ''}`}
            onClick={() => setActiveTab('process')}
          >
            실행 과정
          </button>
          <button 
            className={`tab-btn ${activeTab === 'guide' ? 'active' : ''}`}
            onClick={() => setActiveTab('guide')}
          >
            워크플로우 개발 가이드
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-content">
              <div className="benefits-section">
                <h3>주요 혜택</h3>
                <div className="benefits-grid">
                  {workflow.benefits.map((benefit, index) => (
                    <div key={index} className="benefit-item">
                      <span className="benefit-icon"><Icon icon="mdi:check-circle-outline" width={20} height={20} /></span>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="requirements-section">
                <h3>필요 조건</h3>
                <div className="requirements-list">
                  {workflow.requirements.map((requirement, index) => (
                    <div key={index} className="requirement-item">
                      <span className="requirement-icon"><Icon icon="mdi:clipboard-list-outline" width={20} height={20} /></span>
                      <span>{requirement}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="features-content">
              <div className="features-grid">
                {workflow.features.map((feature, index) => (
                  <div key={index} className="feature-card">
                    <div className="feature-number">{index + 1}</div>
                    <h4>{feature}</h4>
                    <p>AI 기반 자동화로 정확하고 빠른 분석을 제공합니다.</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'process' && (
            <div className="process-content">
                  {isAuthenticated === false ? (
                <div className="process-detail-button-container">
                      <p className="process-detail-notice">
                        로그인하시면 상세한 내용을 확인할 수 있습니다.
                      </p>
                      <button 
                        className="btn-show-process-detail"
                        onClick={() => signInWithRedirect({ provider: 'Google' })}
                      >
                        로그인하기
                      </button>
                </div>
                  ) : isAuthenticated === true ? (
                (id === '3' || id === '5') && !showProcessDetail ? (
                  <div className="process-detail-button-container">
                      <p className="process-detail-notice">
                        워크플로우를 구매하시거나 구독신청 하면 상세한 내용을 확인 할 수 있습니다.
                      </p>
                      <button 
                        className="btn-show-process-detail"
                        onClick={() => setShowProcessDetail(true)}
                      >
                        상세 보기
                      </button>
                    </div>
                ) : (
                <div className="process-timeline">
                  {workflow.steps.map((step, index) => (
                    <div key={index} className="process-step">
                      <div className="step-number">{step.step}</div>
                      <div className="step-content">
                        <h4>{step.title}</h4>
                        <p>{step.description}</p>
                        <span className="step-duration">소요시간: {step.duration}</span>
                      </div>
                    </div>
                  ))}
                </div>
                )
              ) : (
                <div className="process-detail-button-container">
                  <p className="process-detail-notice">로딩 중...</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'guide' && (
            <>
              {(id === '3' || id === '5') && !showGuideDetail ? (
                <div className="process-detail-button-container">
                  {isAuthenticated === false ? (
                    <>
                      <p className="process-detail-notice">
                        로그인하시면 상세한 내용을 확인할 수 있습니다.
                      </p>
                      <button 
                        className="btn-show-process-detail"
                        onClick={() => signInWithRedirect()}
                      >
                        로그인하기
                      </button>
                    </>
                  ) : isAuthenticated === true ? (
                    <>
                      <p className="process-detail-notice">
                        워크플로우를 구매하시거나 구독신청 하면 상세한 내용을 확인 할 수 있습니다.
                      </p>
                      <button 
                        className="btn-show-process-detail"
                        onClick={() => setShowGuideDetail(true)}
                      >
                        상세 보기
                      </button>
                    </>
                  ) : (
                    <div className="process-detail-button-container">
                      <p className="process-detail-notice">로딩 중...</p>
                    </div>
                  )}
                </div>
              ) : id === '3' && showGuideDetail && isAuthenticated === true ? (
                <div className="guide-content">
                  {/* 시스템 개요 */}
                  <div className="guide-section guide-overview">
                    <h3>시스템 개요</h3>
                    <p>n8n과 Google Gemini API를 연동하여, Webhook으로 받은 쿼리(경쟁사명)를 기반으로 AI 분석 리포트를 생성하고, 결과를 Webhook 즉시 응답(요약)과 Airtable(상세)로 이중 전송하는 시스템 구축 가이드입니다.</p>
                    
                    <p>이 가이드에서는 경쟁사명 쿼리 기반 자동 리서치 워크플로우를 구축합니다. 이 워크플로우는 Webhook을 통해 query를 입력받아, Google Gemini의 두 가지 모델을 동시에 활용합니다.</p>
                    
                    <div className="guide-features">
                      <h4>핵심 기능</h4>
                      <ul className="tips-list">
                        <li><strong>Gemini Flash 모델:</strong> 즉각적인 3줄 요약 리포트를 생성하여 Webhook에 바로 응답</li>
                        <li><strong>Gemini Pro 모델:</strong> 상세한 1페이지 분석 리포트를 생성하여 그 결과를 Airtable에 저장</li>
                      </ul>
                    </div>
                  </div>

                  {/* 사전 준비사항 */}
                  <div className="guide-section guide-requirements">
                    <h3>사전 준비사항</h3>
                    
                    <div className="guide-step">
                      <div className="guide-step-number">1</div>
                      <div className="guide-step-content">
                        <h4>n8n 버전 업데이트</h4>
                        <p>Google Gemini API 연동 및 AI 관련 노드의 원활한 사용을 위해 최신 버전의 n8n이 필요할 수 있습니다.</p>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">2</div>
                      <div className="guide-step-content">
                        <h4>API 키 준비</h4>
                        <div className="guide-api-table">
                          <table>
                            <thead>
                              <tr>
                                <th>서비스</th>
                                <th>용도</th>
                                <th>등록 위치</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td><strong>Google Gemini API Key</strong></td>
                                <td>AI 분석 리포트 생성</td>
                                <td>Credentials: <code>googlePalmApi</code></td>
                              </tr>
                              <tr>
                                <td><strong>Airtable Token</strong></td>
                                <td>상세 리포트 저장</td>
                                <td>Credentials: <code>airtableTokenApi</code></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ai query search 에이전트 구축 */}
                  <div className="guide-section">
                    <h3>ai query search 에이전트 구축</h3>
                    
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>트리거 노드 구성 (Triggers)</h4>
                          <p><strong>노드 이름:</strong> Webhook</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.webhook</p>
                          <p><strong>역할:</strong> POST 요청을 통해 외부에서 query 값을 받아 워크플로우를 시작합니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>Path:</strong> 54c8a758-6767-4227-bb67...</li>
                            <li><strong>Response Mode:</strong> responseNode (연결된 'Respond to Webhook' 노드에서 응답 처리)</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>입력 처리 노드</h4>
                          <p><strong>노드 이름:</strong> settings3</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.set</p>
                          <p><strong>역할:</strong> Webhook 노드와 연결되어, 입력받은 body.query 값을 queries라는 변수로 설정하여 워크플로우 내에서 사용하기 쉽게 준비합니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>value:</strong> <code>{`={{ $json.body.query }}`}</code></li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">3</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>AI 모델 노드 (Google Gemini)</h4>
                          <p>이 워크플로우는 settings3 노드 이후 두 개의 브랜치로 나뉘어 각각 다른 Gemini 모델을 호출합니다.</p>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>본문 요약 (flash)</h4>
                          <p><strong>노드 이름:</strong> 본문 요약 (flash)</p>
                          <p><strong>노드 타입:</strong> @n8n/n8n-nodes-langchain.googleGemini</p>
                          <p><strong>Model:</strong> models/gemini-2.5-flash</p>
                          <p><strong>역할:</strong> 빠른 3줄 요약 리포트를 생성하여 Respond to Webhook1 노드로 전달합니다.</p>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>본문 메인 (pro)</h4>
                          <p><strong>노드 이름:</strong> 본문 메인 (pro)</p>
                          <p><strong>노드 타입:</strong> @n8n/n8n-nodes-langchain.googleGemini</p>
                          <p><strong>Model:</strong> models/gemini-2.5-pro</p>
                          <p><strong>역할:</strong> 상세한 1페이지 분석 리포트를 생성하여 검색 내용 저장 (Airtable) 노드로 전달합니다.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">4</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>ai query search 전용 프롬프트</h4>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>유저 프롬프트 (Flash/Pro 공통)</h4>
                          <div className="guide-code">
                            <code>{`={{ $json.queries }} = {{COMPETITOR_NAME}}`}</code>
                          </div>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>시스템 프롬프트 (본문 요약 flash)</h4>
                          <div className="guide-code">
                            <code>{`[프롬프트 이름] 경쟁사 또는 경쟁 서비스 전략 인사이트 1페이지 리포트

[입력값]
- 경쟁사명: {{COMPETITOR_NAME}}
- 출력형식: "3줄 요약 리포트"
---
[명령]
당신은 **스타트업 시장 분석 전문가이자 창업 컨설턴트**입니다.  
유저가 입력한 경쟁사명을 기반으로, 최근 1년 이내의 웹 데이터를 자동으로 검색·분석하여  
창업자가 즉시 전략적 인사이트를 얻을 수 있는 **1페이지 리포트 형태의 결과물만** 작성하라.  
검색 과정이나 URL 리스트는 출력하지 않는다.  
최종 결과는 **요약된 분석 리포트 한 페이지 분량(3줄 이내)**으로 완결되게 제시한다.
---
### 🔍 분석 기준
1. 다음 키워드로 최근 1년 이내 정보를 검색하라:  
   - "{{COMPETITOR_NAME}} 기업 또는 서비스 분석", "{{COMPETITOR_NAME}} 후기 OR 커뮤니티 반응"  
   - "{{COMPETITOR_NAME}} 광고 캠페인 OR 마케팅", "{{COMPETITOR_NAME}} 뉴스 OR 보도자료"
   - "{{COMPETITOR_NAME}} 서비스의 시장 차별점"
   - "{{COMPETITOR_NAME}} 서비스 오류 OR 불만 OR 개선 요청"
2. 커뮤니티, 뉴스, 리뷰, 공식 자료를 교차 검증하여 신뢰도 높은 인사이트만 활용한다.  
3. 반복되는 키워드, 감정 표현, 기능 요청 등을 정량적 신호로 해석하여 SWOT+GTM+Talent Flow 분석을 수행한다.  
---
### 📄 **최종 출력 형식 (OUTPUT FORMAT = "요약 리포트")**
📘 **경쟁사 또는 경쟁 서비스 전략 인사이트 리포트** **대상:** {{COMPETITOR_NAME}} 
**분석기간:** 최근 1년 이내 
**작성목적:** 창업자의 전략 수립 및 차별화 포인트 도출  
---
#### 🔎 핵심 요약 (Executive Summary)
- (3줄 이내로 핵심 포인트 요약: 최근 시장 반응, 주요 변화, 해당 서비스 또는 경쟁사를 뛰어 넘을 수 있는 핵심 공략 포인트)
---
[추가 지시사항]
- 절대 중간 검색 과정, URL, 데이터 목록은 출력하지 않는다.  
- 결과는 **하나의 완결된 리포트 형태**로만 제시한다.  
- 톤앤매너는 객관적이지만, 창업자에게 **결정적 통찰**을 주는 방식으로 작성하라.  
- 불필요한 수식어나 장식 없이 명확하고 임팩트 있게 표현하라.
- 문장은 가독성이 높도록 줄바꿈(\\n)을 활용해서 작성하라. 

---`}</code>
                          </div>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>시스템 프롬프트 (본문 메인 pro)</h4>
                          <div className="guide-code">
                            <code>{`[프롬프트 이름] 경쟁사 또는 경쟁 서비스 전략 인사이트 1페이지 리포트

[입력값]
- 경쟁사명: {{COMPETITOR_NAME}}
- 출력형식: "1페이지 리포트" (고정)
---
[명령]
당신은 **스타트업 시장 분석 전문가이자 창업 컨설턴트**입니다.  
유저가 입력한 경쟁사명을 기반으로, 최근 1년 이내의 웹 데이터를 자동으로 검색·분석하여  
창업자가 즉시 전략적 인사이트를 얻을 수 있는 **1페이지 리포트 형태의 결과물만** 작성하라.  
검색 과정이나 URL 리스트는 출력하지 않는다.  
최종 결과는 **요약된 분석 리포트 한 페이지 분량(약 500~700자)**으로 완결되게 제시한다.
---
### 🔍 분석 기준
1. 다음 키워드로 최근 1년 이내 정보를 검색하라:  
   - "{{COMPETITOR_NAME}} 기업 또는 서비스 분석", "{{COMPETITOR_NAME}} 후기 OR 커뮤니티 반응"  
   - "{{COMPETITOR_NAME}} 광고 캠페인 OR 마케팅", "{{COMPETITOR_NAME}} 뉴스 OR 보도자료"
   - "{{COMPETITOR_NAME}} 서비스의 시장 차별점"
   - "{{COMPETITOR_NAME}} 서비스 오류 OR 불만 OR 개선 요청"
2. 커뮤니티, 뉴스, 리뷰, 공식 자료를 교차 검증하여 신뢰도 높은 인사이트만 활용한다.  
3. 반복되는 키워드, 감정 표현, 기능 요청 등을 정량적 신호로 해석하여 SWOT+GTM+Talent Flow 분석을 수행한다.  
---
### 📄 **최종 출력 형식 (OUTPUT FORMAT = "1페이지 리포트")**
📘 **경쟁사 또는 경쟁 서비스 전략 인사이트 리포트** **대상:** {{COMPETITOR_NAME}}  
**분석기간:** 최근 1년 이내  
**작성목적:** 창업자의 전략 수립 및 차별화 포인트 도출  
---
#### 🔎 핵심 요약 (Executive Summary)
- (3줄 이내로 핵심 포인트 요약: 최근 시장 반응, 주요 변화, 핵심 인사이트)
---
#### 📉 약점 (Weakness)
- (서비스, UX, 고객 반응 등 약점 요약 2~3개)
#### 💡 기회 (Opportunity)
- (시장 혹은 고객이 원하는 기능/기회 포인트 2~3개)
#### 📈 마케팅 분석 (Go-To-Market)
- ✅ **성공사례:** (캠페인, 콘텐츠, 반응 요약)  
- ⚠️ **실패사례:** (문제점, 부정 피드백 요약)
#### 👥 인재 이동 (Talent Flow)
- (최근 채용 및 팀 확장 방향 한 줄 요약)
#### 🚀 다음 전략 예측 (Next Move)
- (단기·중기적 전략 추론 요약 1~2줄)
---
#### 🧭 창업자 인사이트 (Founder's Insight)
- **주의할 점:** (예: 시장 진입 시 UX 최적화와 고객 피드백 대응 중요)  
- **차별화 포인트:** (예: AI 면접 코칭, 사용자 참여형 피드백 시스템 등)  
- **요약 문장:** (이 경쟁사의 전략적 방향성과 창업자에게 주는 교훈 2~3줄로 요약)
---
[추가 지시사항]
- 절대 중간 검색 과정, URL, 데이터 목록은 출력하지 않는다.  
- 결과는 **하나의 완결된 리포트 형태**로만 제시한다.  
- 톤앤매너는 객관적이지만, 창업자에게 **결정적 통찰**을 주는 방식으로 작성하라.  
- 불필요한 수식어나 장식 없이 명확하고 임팩트 있게 표현하라.  (예시: 네 알겠습니다, ~시작하겠습니다. (x))
- 문장은 가독성이 높도록 줄바꿈(\\n)을 활용해서 작성하라. 
---`}</code>
                          </div>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">5</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>구조화된 출력 설정</h4>
                          <p>이 워크플로우는 별도의 'Structured Output Parser' 노드를 사용하는 대신, 각 Gemini 노드의 시스템 프롬프트 내에서 직접 텍스트 기반 리포트 형식을(마크다운) 지정합니다. AI는 프롬프트에 정의된 형식에 맞춰 결과를 생성합니다.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">6</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>최종 목적지 연동 설정</h4>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>1. Webhook 즉시 응답</h4>
                          <p><strong>노드 이름:</strong> Respond to Webhook1</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.respondToWebhook</p>
                          <p><strong>역할:</strong> 본문 요약 (flash) 노드 뒤에 연결됩니다. Flash 모델이 생성한 3줄 요약 리포트를 Webhook을 호출한 사용자에게 즉시 반환합니다.</p>
                          <p><strong>응답 데이터:</strong> <code>{`{{ $json.content.parts[0].text }}`}</code></p>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>2. Airtable 상세 내용 저장</h4>
                          <p><strong>노드 이름:</strong> 검색 내용 저장</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.airtable</p>
                          <p><strong>Operation:</strong> create</p>
                          <p><strong>Base:</strong> appkQfDxm....</p>
                          <p><strong>Table:</strong> tblP1msFMJY0....</p>
                          <p><strong>Field Mapping (필드 매핑):</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>생성일:</strong> <code>{`={{ $('Webhook').item.json.body.timestamp }}`}</code></li>
                            <li><strong>query:</strong> <code>{`={{ $('settings3').item.json.queries }}`}</code></li>
                            <li><strong>research_result:</strong> <code>{`={{ $json.content.parts[0].text }}`}</code></li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 주의사항 및 팁 */}
                  <div className="guide-section guide-warnings">
                    <h3>주의사항 및 팁</h3>
                    <div className="guide-tips">
                      <h4>성능 최적화</h4>
                      <h4 style={{ marginTop: '15px', fontSize: '1rem' }}>모델 선택 전략:</h4>
                      <p>빠른 응답이 필요한 경우(Webhook 응답) Gemini Flash를, 상세하고 깊이 있는 분석이 필요한 경우(Airtable 저장) Gemini Pro를 사용하는 현재 구조는 비용과 성능의 균형을 맞춘 좋은 예입니다.</p>
                      
                      <h4 style={{ marginTop: '15px', fontSize: '1rem' }}>비용 관리:</h4>
                      <p>Gemini Pro 모델은 Flash 모델보다 비용이 높으므로, 상세 리포트가 매번 필요한지 검토하고 워크플로우를 최적화하세요.</p>
                      
                      <h4>트러블슈팅</h4>
                      <h4 style={{ marginTop: '15px', fontSize: '1rem' }}>타임아웃 오류:</h4>
                      <p>본문 메인 (pro) 노드의 프롬프트가 매우 상세하고 복잡한 분석을 요구하므로, Gemini API 응답 시간이 길어질 수 있습니다. 타임아웃이 발생하면 프롬프트의 분석 기준을 일부 축소하거나 단순화해 보세요.</p>
                      
                      <h4 style={{ marginTop: '15px', fontSize: '1rem' }}>응답 품질 저하:</h4>
                      <p>리포트 형식이 깨지거나 원하는 내용이 나오지 않는 경우, 시스템 프롬프트의 "최종 출력 형식" 부분을 더 명확하게 다듬거나 예시를 보강해야 합니다.</p>
                    </div>
                  </div>

                  {/* AI 검색 영역 */}
                  <div className="ai-search-section">
                    <div className="ai-search-header">
                      <h3 className="ai-search-title">AI 검색</h3>
                      <p className="ai-search-description">
                        워크플로우 개발에 대한 궁금한 점을 검색해보세요. RAG 기반 AI가 관련 정보를 제공합니다.
                      </p>
                      {isAuthenticated === true && (
                        <p className="ai-search-limit-info">
                          하루 {MAX_SEARCHES_PER_DAY}회 제한 (오늘 {searchCount}/{MAX_SEARCHES_PER_DAY}회 사용)
                        </p>
                      )}
                    </div>
                    
                    <div className="ai-search-container">
                      {isAuthenticated !== true ? (
                        <div className="ai-search-login-required">
                          <p className="ai-search-login-message">
                            <Icon icon="mdi:lock-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> 로그인이 필요합니다. 로그인 후 AI 검색을 사용할 수 있습니다.
                          </p>
                          <button 
                            className="ai-search-login-btn"
                            onClick={() => signInWithRedirect()}
                          >
                            로그인하기
                          </button>
                        </div>
                      ) : (
                        <>
                          <form onSubmit={handleSearchSubmit} className="ai-search-form">
                            <div className="ai-search-input-wrapper">
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="워크플로우 개발에 대한 궁금한 점을 검색하세요..."
                                className="ai-search-input"
                                disabled={isSearching || searchLimitReached}
                              />
                              <button 
                                type="submit" 
                                className="ai-search-btn"
                                disabled={!searchQuery.trim() || isSearching || searchLimitReached}
                              >
                                {isSearching ? <Icon icon="mdi:loading" width={18} height={18} className="spin-icon" /> : <Icon icon="mdi:magnify" width={18} height={18} />}
                              </button>
                            </div>
                          </form>
                          
                          {/* 검색 결과 표시 */}
                          {isSearching && (
                            <div className="ai-search-loading">
                              <div className="loading-spinner"></div>
                              <p>AI가 검색 중입니다...</p>
                            </div>
                          )}
                          
                          {searchError && (
                            <div className="ai-search-error">
                              <p><Icon icon="mdi:close-circle-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />{searchError}</p>
                            </div>
                          )}
                          
                          {searchLimitReached && !isSearching && (
                            <div className="ai-search-limit-message">
                              <p><Icon icon="mdi:alert-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />하루 최대 {MAX_SEARCHES_PER_DAY}회 검색 제한에 도달했습니다. 내일 다시 시도해주세요.</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : id === '5' && showGuideDetail && isAuthenticated === true ? (
                <div className="guide-content">
                  {/* 시스템 개요 */}
                  <div className="guide-section guide-overview">
                    <h3>시스템 개요</h3>
                    <p>n8n과 Google Gemini API를 연동하여, 웹 크롤링을 통해 최신 콘텐츠를 수집하고, 이를 기반으로 SEO 최적화된 블로그 포스트를 자동 생성하며, 생성된 콘텐츠와 이미지를 WordPress, Medium, Tistory 등 다양한 블로그 플랫폼에 자동으로 게시하는 AI 에이전트 시스템의 상세 구축 가이드입니다.</p>
                    
                    <p>본 워크플로우는 두 개의 주요 단계로 구성됩니다.</p>
                    
                    <div className="guide-features">
                      <h4>1단계: 웹 크롤링 및 데이터 전처리</h4>
                      <p>매일 오전 10시, 설정한 웹사이트나 API 엔드포인트에서 최신 콘텐츠를 크롤링합니다. 이후 수집된 데이터를 표준화(title, content, date)하고, 블로그 작성에 적합한 형태로 전처리합니다.</p>
                      
                      <h4>2단계: AI 블로그 포스트 생성 및 자동 게시</h4>
                      <p>전처리된 콘텐츠를 Google Gemini 2.5 Pro 모델을 탑재한 AI Agent에게 전달하여, 구조화된 블로그 포스트(제목, 본문, 태그, 메타 설명, 이미지 프롬프트)를 JSON 형식으로 생성합니다. 이어서 Google Gemini 2.5 Flash 모델이 이미지 프롬프트를 받아 이미지를 생성하고, 최종 텍스트와 이미지는 설정한 블로그 플랫폼의 API를 통해 자동으로 게시됩니다.</p>
                    </div>
                  </div>

                  {/* 사전 준비사항 */}
                  <div className="guide-section guide-requirements">
                    <h3>사전 준비사항</h3>
                    
                    <div className="guide-step">
                      <div className="guide-step-number">1</div>
                      <div className="guide-step-content">
                        <h4>n8n 버전</h4>
                        <p>최신 n8n 버전(≥ 1.58.0) 권장</p>
                        <p>Docker 또는 클라우드 환경 모두 지원</p>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">2</div>
                      <div className="guide-step-content">
                        <h4>필수 API Key</h4>
                        <div className="guide-api-table">
                          <table>
                            <thead>
                              <tr>
                                <th>서비스</th>
                                <th>용도</th>
                                <th>등록 위치</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td><strong>Google Gemini API Key</strong></td>
                                <td>블로그 포스트 생성 및 이미지 생성</td>
                                <td>Credentials: <code>googlePalmApi</code></td>
                              </tr>
                              <tr>
                                <td><strong>웹 크롤링 API</strong></td>
                                <td>콘텐츠 수집</td>
                                <td>HTTP Request 노드</td>
                              </tr>
                              <tr>
                                <td><strong>블로그 플랫폼 API</strong></td>
                                <td>자동 게시</td>
                                <td>WordPress/Medium/Tistory API</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p style={{ marginTop: '15px' }}><strong>참고:</strong> 워크플로우 JSON을 기반으로 다음 Credential이 필요합니다.</p>
                        <ul className="tips-list" style={{ paddingLeft: '20px', marginTop: '10px' }}>
                          <li><strong>Google Gemini API Key:</strong> Google Gemini Chat Model 및 Generate an image 노드용</li>
                          <li>JSON 내 Credential 이름: <code>Gemini PaLM 키 이름</code> 또는 <code>googlePalmApi</code></li>
                        </ul>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">3</div>
                      <div className="guide-step-content">
                        <h4>환경 변수 설정</h4>
                        <div className="guide-code">
                          <code>{`GEMINI_API_KEY=gai-xxxxxx
BLOG_PLATFORM_API_KEY=your-api-key
BLOG_PLATFORM_URL=https://your-blog.com`}</code>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 워크플로우 개요 */}
                  <div className="guide-section">
                    <h3>워크플로우 개요</h3>
                    <p>이 워크플로우는 총 <strong>13개 노드</strong>로 구성되어 있으며, 다음 순서로 실행됩니다:</p>
                    
                    <div className="guide-node-list">
                      <ol className="tips-list" style={{ paddingLeft: '20px' }}>
                        <li><strong>Schedule Trigger</strong> — 자동 실행 스케줄 지정 (예: 매일 10시)</li>
                        <li><strong>HTTP Request</strong> — 웹 크롤링을 통한 콘텐츠 수집</li>
                        <li><strong>Code</strong> — 수집된 데이터 전처리 및 파싱</li>
                        <li><strong>Split Out</strong> — 여러 콘텐츠를 개별 아이템으로 분리</li>
                        <li><strong>Aggregate</strong> — 전처리된 콘텐츠 데이터 집계</li>
                        <li><strong>blog 프롬프트 불러오기 (Airtable)</strong> — AI Agent용 시스템 프롬프트 로드</li>
                        <li><strong>AI Agent</strong> — Google Gemini 2.5 Pro를 사용한 블로그 포스트 생성</li>
                        <li><strong>Google Gemini Chat Model</strong> — 실제 텍스트 생성 수행</li>
                        <li><strong>Code in JavaScript</strong> — AI 응답에서 JSON 파싱</li>
                        <li><strong>(선택) 이미지 생성 파이프라인</strong> — Gemini 2.5 Flash로 이미지 생성</li>
                        <li><strong>Edit Fields</strong> — 블로그 포스트 필드 정리 (제목, 본문, 태그 등)</li>
                        <li><strong>HTTP Request (블로그 API)</strong> — 생성된 포스트를 블로그 플랫폼에 게시</li>
                        <li><strong>IF</strong> — 게시 성공 여부 확인 및 알림</li>
                      </ol>
                    </div>
                  </div>

                  {/* 크롤링 및 블로그 작성 에이전트 구축 */}
                  <div className="guide-section">
                    <h3>뉴스 크롤링 및 블로그 작성 에이전트 구축</h3>
                    
                    <h4 style={{ marginTop: '30px', marginBottom: '20px', fontSize: '1.5rem', fontWeight: '600' }}>1단계: 웹 크롤링 및 데이터 전처리</h4>
                    <p>첫 번째 단계는 자동화의 기반이 되는 최신 콘텐츠를 수집하고 정제하는 과정입니다.</p>
                    
                    <div className="workflow-image-container" style={{ marginTop: '20px', marginBottom: '30px' }}>
                      <img 
                        src="/images/workflow/workflow_blog_step1.png" 
                        alt="워크플로우 1단계: 웹 크롤링 및 데이터 전처리" 
                        className="workflow-diagram-image"
                        onClick={() => setEnlargedImage({
                          src: "/images/workflow/workflow_blog_step1.png",
                          alt: "워크플로우 1단계: 웹 크롤링 및 데이터 전처리"
                        })}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>

                  {/* 노드 구성 상세 */}
                  <div className="guide-section">
                    <h3>노드 구성 상세</h3>
                    
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Schedule Trigger (스케줄 실행)</h4>
                          <p><strong>노드 이름:</strong> Schedule Trigger</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.scheduleTrigger</p>
                          <p><strong>역할:</strong> 워크플로우를 매일 특정 시간에 자동 실행합니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>Trigger At Hour:</strong> 10 (매일 오전 10시에 실행)</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>HTTP Request (웹 크롤링)</h4>
                          <p><strong>노드 이름:</strong> HTTP Request</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.httpRequest</p>
                          <p><strong>역할:</strong> 설정한 웹사이트 URL 또는 API 엔드포인트에서 최신 콘텐츠를 크롤링합니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>Method:</strong> GET 또는 POST</li>
                            <li><strong>URL:</strong> 크롤링할 웹사이트 URL 또는 API 엔드포인트</li>
                          </ul>
                          <p><strong>설정 예시:</strong></p>
                          <div className="guide-code">
                            <code>{`{
  "url": "https://api.example.com/articles",
  "method": "GET",
  "headers": {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
  }
}`}</code>
                          </div>
                          <p><strong>출력 필드:</strong> <code>title</code>, <code>content</code>, <code>url</code>, <code>published_date</code></p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">3</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Code (데이터 전처리)</h4>
                          <p><strong>노드 이름:</strong> Code</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.code</p>
                          <p><strong>역할:</strong> 수집된 데이터를 블로그 작성에 적합한 형태로 변환하고 표준화합니다.</p>
                          <p><strong>주요 파라미터 (JavaScript Code):</strong></p>
                          <div className="guide-code">
                            <code>{`const items = $input.all();
return items.map(item => ({
  title: item.json.title || item.json.headline,
  content: item.json.content || item.json.description || item.json.body,
  url: item.json.url || item.json.link,
  date: item.json.published_date || item.json.pubDate || item.json.created_at
}));`}</code>
                          </div>
                          <p><strong>출력 예시:</strong></p>
                          <div className="guide-code">
                            <code>{`{
  "title": "AI 기술의 최신 동향",
  "content": "최근 AI 기술이 급속히 발전하고 있습니다...",
  "url": "https://example.com/article123",
  "date": "2025-01-15T10:00:00Z"
}`}</code>
                          </div>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">4</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Split Out (콘텐츠 분리)</h4>
                          <p><strong>노드 이름:</strong> Split Out</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.splitOut</p>
                          <p><strong>역할:</strong> 여러 콘텐츠를 개별 아이템으로 분리하여 각각 블로그 포스트를 생성합니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>Field To Split Out:</strong> (배열 필드가 있는 경우) 또는 전체 아이템을 개별로 분리</li>
                          </ul>
                        </div>
                      </div>

                      <h4 style={{ marginTop: '40px', marginBottom: '20px', fontSize: '1.5rem', fontWeight: '600' }}>2단계: AI 블로그 포스트 생성 및 자동 게시</h4>
                      <p>전처리된 데이터를 바탕으로 AI가 블로그 포스트를 작성하고, 최종 목적지인 블로그 플랫폼에 자동으로 게시합니다.</p>
                      
                      <div className="workflow-image-container" style={{ marginTop: '20px', marginBottom: '30px' }}>
                        <img 
                          src="/images/workflow/workflow_blog_step2.png" 
                          alt="워크플로우 2단계: AI 블로그 포스트 생성 및 자동 게시" 
                          className="workflow-diagram-image"
                          onClick={() => setEnlargedImage({
                            src: "/images/workflow/workflow_blog_step2.png",
                            alt: "워크플로우 2단계: AI 블로그 포스트 생성 및 자동 게시"
                          })}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">5</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Aggregate (데이터 집계)</h4>
                          <p><strong>노드 이름:</strong> Aggregate</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.aggregate</p>
                          <p><strong>역할:</strong> 1단계에서 전처리된 모든 콘텐츠 아이템(JSON)을 data라는 키를 가진 단일 JSON 배열로 집계합니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>Aggregate:</strong> aggregateAllItemData</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">6</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>blog 프롬프트 불러오기 (프롬프트 로드)</h4>
                          <p><strong>노드 이름:</strong> blog 프롬프트 불러오기</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.airtable</p>
                          <p><strong>역할:</strong> AI Agent가 사용할 시스템 프롬프트를 Airtable에서 동적으로 불러옵니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>Operation:</strong> search</li>
                            <li><strong>Table:</strong> Prompt 모음 (또는 사용자 정의 테이블명)</li>
                            <li><strong>Filter By Formula:</strong> <code>case = "blog_new"</code></li>
                            <li><strong>출력 필드:</strong> <code>system_prompt</code></li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">7</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>AI Agent (블로그 초안 생성)</h4>
                          <p><strong>노드 이름:</strong> AI Agent</p>
                          <p><strong>노드 타입:</strong> @n8n/n8n-nodes-langchain.agent</p>
                          <p><strong>역할:</strong> 집계된 콘텐츠 데이터와 Airtable 프롬프트를 조합하여 Gemini 모델에게 블로그 포스트 생성을 요청합니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>Prompt Type:</strong> define</li>
                            <li><strong>Text (유저 프롬프트):</strong> <code>{`{{ JSON.stringify($('Aggregate').item.json.data) }}`}</code></li>
                            <li><strong>Options &gt; System Message:</strong> <code>{`{{ $json.system_prompt }}`}</code> (6번 노드에서 로드한 프롬프트)</li>
                            <li><strong>연결된 노드 (Model):</strong> Google Gemini Chat Model</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">8</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Google Gemini Chat Model (AI 모델)</h4>
                          <p><strong>노드 이름:</strong> Google Gemini Chat Model</p>
                          <p><strong>노드 타입:</strong> @n8n/n8n-nodes-langchain.lmChatGoogleGemini</p>
                          <p><strong>역할:</strong> AI Agent의 요청을 받아 실제 텍스트 생성을 수행하는 모델입니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>Model Name:</strong> <code>models/gemini-2.5-pro</code></li>
                          </ul>
                          <p><strong>System Prompt 예시 (Airtable에서 로드):</strong></p>
                          <div className="guide-code">
                            <code>{`다음 콘텐츠를 분석하여 SEO 최적화된 블로그 포스트를 작성해주세요.

요구사항:
- 제목: 검색 엔진 최적화를 고려한 매력적인 제목 (50자 이내)
- 본문: 800-1200자 분량의 상세한 내용, 자연스러운 한국어로 작성
- 태그: 관련 키워드 3-5개
- 메타 설명: 150자 이내의 요약
- 이미지 프롬프트: 블로그 포스트에 적합한 이미지 프롬프트 2-3개

출력 형식: 반드시 JSON 형식으로만 출력
{
  "content": {
    "제목": "블로그 제목",
    "본문": "블로그 본문 내용",
    "태그": ["태그1", "태그2", "태그3"],
    "메타 설명": "메타 설명"
  },
  "sub": {
    "블로그 사진 프롬프트": ["프롬프트1", "프롬프트2", "프롬프트3"]
  }
}`}</code>
                          </div>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">9</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Code in JavaScript (JSON 파싱)</h4>
                          <p><strong>노드 이름:</strong> Code in JavaScript</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.code</p>
                          <p><strong>역할:</strong> AI Agent가 반환한 텍스트 응답(Markdown 코드 블럭 포함)에서 순수한 JSON 객체만 추출하고 파싱합니다.</p>
                          <p><strong>주요 파라미터 (JavaScript Code):</strong></p>
                          <div className="guide-code">
                            <code>{`// 1. 입력 아이템을 가져옵니다.
const item = $input.first().json;
const rawOutput = item.message?.content || item.content || item.text || JSON.stringify(item);

// 2. 코드 블록 사이의 내용을 추출하는 정규식입니다.
const regex = /\\\`\\\`\\\`json\\n([\\s\\S]*)\\n\\\`\\\`\\\`/;
const match = rawOutput.match(regex);

// 3. 정규식이 일치하는 내용을 찾았는지 확인합니다.
if (match && match[1]) {
  const jsonString = match[1];
  try {
    const parsedJson = JSON.parse(jsonString);
    return [{ json: parsedJson }];
  } catch (error) {
    item.error = "추출된 JSON 문자열을 파싱하는 데 실패했습니다: " + error.message;
    return [{ json: item }];
  }
} else {
  try {
    const parsedJson = JSON.parse(rawOutput);
    return [{ json: parsedJson }];
  } catch (error) {
    item.error = "입력 텍스트에서 JSON을 찾지 못했습니다.";
    return [{ json: item }];
  }
}`}</code>
                          </div>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">10</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>(선택) 이미지 생성 파이프라인</h4>
                          <p>이 단계는 블로그 포스트의 이미지 프롬프트를 기반으로 <strong>Gemini 이미지 모델</strong>을 활용하여 썸네일 이미지를 자동 생성합니다.</p>
                          <p><strong>구성 흐름:</strong></p>
                          <p><code>Edit Fields2 (이미지 프롬프트 준비)</code> → <code>Split Out (프롬프트 분할)</code> → <code>Generate an image (Gemini)</code> → <code>Aggregate (이미지 링크 집계)</code></p>
                          <p><strong>출력 이미지 모델:</strong> <code>models/gemini-2.5-flash-image</code></p>
                          <p><strong>Negative Prompt 규칙:</strong> "Avoid distorted fingers, broken text, or unwanted artifacts."</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">11</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Edit Fields (필드 매핑)</h4>
                          <p><strong>노드 이름:</strong> Edit Fields</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.set</p>
                          <p><strong>역할:</strong> 파싱된 JSON 데이터를 블로그 플랫폼 API 형식에 맞게 필드 매핑합니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><code>title</code>: <code>{`{{ $json.content['제목'] }}`}</code> (Gemini에서 생성된 제목)</li>
                            <li><code>content</code>: <code>{`{{ $json.content['본문'] }}`}</code> (Gemini에서 생성된 본문)</li>
                            <li><code>tags</code>: <code>{`{{ $json.content['태그'] }}`}</code> (Gemini에서 생성된 태그 배열)</li>
                            <li><code>meta_description</code>: <code>{`{{ $json.content['메타 설명'] }}`}</code></li>
                            <li><code>status</code>: "publish" 또는 "draft"</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">12</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>HTTP Request (블로그 게시)</h4>
                          <p><strong>노드 이름:</strong> HTTP Request</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.httpRequest</p>
                          <p><strong>역할:</strong> 생성된 블로그 포스트를 설정한 블로그 플랫폼의 API를 통해 자동으로 게시합니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>Method:</strong> POST</li>
                            <li><strong>URL:</strong> 블로그 플랫폼 API 엔드포인트</li>
                          </ul>
                          <p><strong>예시 (WordPress):</strong></p>
                          <div className="guide-code">
                            <code>{`{
  "url": "https://your-blog.com/wp-json/wp/v2/posts",
  "method": "POST",
  "headers": {
    "Authorization": "Bearer YOUR_WP_TOKEN",
    "Content-Type": "application/json"
  },
  "body": {
    "title": "{{ $json.title }}",
    "content": "{{ $json.content }}",
    "status": "publish",
    "tags": {{ $json.tags }},
    "meta": {
      "description": "{{ $json.meta_description }}"
    }
  }
}`}</code>
                          </div>
                          <p><strong>예시 (Medium):</strong></p>
                          <div className="guide-code">
                            <code>{`{
  "url": "https://api.medium.com/v1/users/{userId}/posts",
  "method": "POST",
  "headers": {
    "Authorization": "Bearer YOUR_MEDIUM_TOKEN",
    "Content-Type": "application/json"
  },
  "body": {
    "title": "{{ $json.title }}",
    "contentFormat": "html",
    "content": "{{ $json.content }}",
    "tags": {{ $json.tags }},
    "publishStatus": "public"
  }
}`}</code>
                          </div>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">13</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>IF (성공 여부 확인)</h4>
                          <p><strong>노드 이름:</strong> IF</p>
                          <p><strong>노드 타입:</strong> n8n-nodes-base.if</p>
                          <p><strong>역할:</strong> 게시 성공 여부를 확인하고 실패 시 알림을 전송합니다.</p>
                          <p><strong>주요 파라미터:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>조건:</strong> <code>$json.id !== undefined</code> (게시 성공 시 ID 반환)</li>
                            <li><strong>True 경로:</strong> 성공 알림 또는 다음 워크플로우 실행</li>
                            <li><strong>False 경로:</strong> 실패 알림 (이메일, Slack 등)</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 블로그 플랫폼 연동 */}
                  <div className="guide-section guide-airtable-info">
                    <h3>블로그 플랫폼 연동</h3>
                    <ul className="tips-list">
                      <li><strong>지원 플랫폼:</strong>
                        <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
                          <li>WordPress (REST API)</li>
                          <li>Medium (Integration Token)</li>
                          <li>Tistory (OAuth 2.0)</li>
                          <li>Ghost (Admin API)</li>
                        </ul>
                      </li>
                      <li><strong>API 인증:</strong>
                        <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
                          <li>WordPress: Application Password 또는 OAuth</li>
                          <li>Medium: Integration Token</li>
                          <li>Tistory: OAuth 2.0 토큰</li>
                        </ul>
                      </li>
                    </ul>
                  </div>

                  {/* 자동화 스케줄 설정 */}
                  <div className="guide-section">
                    <h3>자동화 스케줄 설정</h3>
                    <p>매일 오전 10:00 콘텐츠 크롤링 및 블로그 자동 게시</p>
                    <p>필요 시 n8n Cron 설정:</p>
                    <div className="guide-code">
                      <code>0 10 * * *</code>
                    </div>
                  </div>

                  {/* 주의사항 및 팁 */}
                  <div className="guide-section guide-warnings">
                    <h3>주의사항 및 팁</h3>
                    <div className="guide-tips">
                      <h4>1. JSON 파싱 안정성</h4>
                      <p>이 워크플로우의 핵심이자 가장 오류가 발생하기 쉬운 부분은 Code in JavaScript 노드입니다. AI Agent가 반환하는 텍스트가 <code>```json\n... \n```</code> 형식을 정확히 따르지 않으면 파싱에 실패합니다.</p>
                      <ul className="tips-list">
                        <li><strong>해결책:</strong> blog 프롬프트 불러오기 노드가 Airtable에서 가져오는 system_prompt의 내용을 명확하게 수정하여, AI가 반드시 지정된 JSON 코드 블록 형식으로만 응답하도록 유도해야 합니다.</li>
                        <li>System Prompt에 "반드시 JSON 형식으로만 출력" 또는 "출력 형식 이외의 다른 내용은 절대로 추가하지 말 것"과 같은 명확한 지시를 포함하세요.</li>
                        <li>파싱 실패 시를 대비해 에러 핸들링 로직을 추가하는 것이 좋습니다.</li>
                      </ul>
                      
                      <h4>2. 크롤링 에티켓 및 법적 고려사항</h4>
                      <ul className="tips-list">
                        <li><strong>robots.txt 확인:</strong> 크롤링 전 대상 웹사이트의 robots.txt를 확인하고 준수하세요.</li>
                        <li><strong>요청 딜레이:</strong> 과도한 요청 방지를 위한 딜레이 설정 (예: 요청 간 1-2초 대기)</li>
                        <li><strong>저작권 존중:</strong> 원본 콘텐츠의 저작권을 존중하고, 출처를 명시하세요.</li>
                        <li><strong>이용약관 확인:</strong> 크롤링 대상 웹사이트의 이용약관을 확인하여 자동화된 크롤링이 허용되는지 확인하세요.</li>
                      </ul>
                      
                      <h4>3. API 비용 및 할당량 관리</h4>
                      <p>이 워크플로우는 실행 당 여러 번의 유료 API 호출을 발생시킵니다.</p>
                      <ul className="tips-list">
                        <li><strong>Gemini 2.5 Pro:</strong> 1회 (텍스트 생성)</li>
                        <li><strong>Gemini 2.5 Flash:</strong> N회 (이미지 프롬프트 배열의 개수만큼)</li>
                        <li><strong>블로그 플랫폼 API:</strong> 플랫폼별 Rate Limit 확인 (예: WordPress는 초당 5회 이하 권장)</li>
                        <li><strong>비용 최적화:</strong> Split Out 노드에서 분할되는 이미지 프롬프트의 수를 2~3개로 제한하는 것이 비용 관리에 유리합니다.</li>
                        <li><strong>모니터링:</strong> API 사용량을 정기적으로 모니터링하여 예상치 못한 비용 발생을 방지하세요.</li>
                      </ul>
                      
                      <h4>4. 콘텐츠 품질 관리</h4>
                      <ul className="tips-list">
                        <li><strong>사전 검토:</strong> 생성된 콘텐츠를 자동 게시하기 전에 사전 검토하는 단계를 추가하는 것을 권장합니다.</li>
                        <li><strong>System Prompt 조정:</strong> System Prompt를 통해 작성 스타일, 톤, 길이 등을 조정할 수 있습니다.</li>
                        <li><strong>중복 방지:</strong> 이미 게시한 콘텐츠와 중복되지 않도록 체크 로직을 추가하세요.</li>
                        <li><strong>A/B 테스트:</strong> 다양한 System Prompt를 테스트하여 최적의 콘텐츠 품질을 찾으세요.</li>
                      </ul>
                      
                      <h4>5. SEO 최적화</h4>
                      <ul className="tips-list">
                        <li><strong>키워드 연구:</strong> 타겟 키워드를 System Prompt에 포함하여 SEO 최적화된 콘텐츠를 생성하세요.</li>
                        <li><strong>메타 태그:</strong> 메타 설명, Open Graph 태그 등을 자동으로 생성하도록 설정하세요.</li>
                        <li><strong>이미지 최적화:</strong> 이미지 alt 텍스트를 자동으로 추가하고, 이미지 크기를 최적화하세요.</li>
                        <li><strong>내부 링크:</strong> 관련 블로그 포스트로의 내부 링크를 자동으로 추가하는 로직을 고려하세요.</li>
                      </ul>
                      
                      <h4>6. 테스트 및 디버깅</h4>
                      <ul className="tips-list">
                        <li><strong>테스트 모드:</strong> 처음 워크플로우를 구축할 때는 "draft" 상태로 게시하여 결과를 확인하세요.</li>
                        <li><strong>로그 확인:</strong> 각 노드의 실행 로그를 확인하여 데이터 흐름을 추적하세요.</li>
                        <li><strong>에러 핸들링:</strong> 각 단계에서 발생할 수 있는 에러를 처리하는 로직을 추가하세요.</li>
                        <li><strong>점진적 확장:</strong> 소수의 콘텐츠로 시작하여 점진적으로 확장하세요.</li>
                      </ul>
                    </div>
                  </div>

                  {/* 확장 아이디어 */}
                  <div className="guide-section guide-resources">
                    <h3>확장 아이디어</h3>
                    <ul className="tips-list">
                      <li>소셜 미디어 자동 공유 (Twitter, LinkedIn, Facebook)</li>
                      <li>이미지 자동 생성 및 삽입 (DALL-E, Midjourney)</li>
                      <li>댓글 자동 응답 시스템</li>
                      <li>콘텐츠 성과 분석 및 리포트 생성</li>
                      <li>다국어 블로그 포스트 자동 생성</li>
                    </ul>
                  </div>

                  {/* AI 검색 영역 */}
                  <div className="ai-search-section">
                    <div className="ai-search-header">
                      <h3 className="ai-search-title">AI 검색</h3>
                      <p className="ai-search-description">
                        워크플로우 개발에 대한 궁금한 점을 검색해보세요. RAG 기반 AI가 관련 정보를 제공합니다.
                      </p>
                      {isAuthenticated === true && (
                        <p className="ai-search-limit-info">
                          하루 {MAX_SEARCHES_PER_DAY}회 제한 (오늘 {searchCount}/{MAX_SEARCHES_PER_DAY}회 사용)
                        </p>
                      )}
                    </div>
                    
                    <div className="ai-search-container">
                      {isAuthenticated !== true ? (
                        <div className="ai-search-login-required">
                          <p className="ai-search-login-message">
                            <Icon icon="mdi:lock-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> 로그인이 필요합니다. 로그인 후 AI 검색을 사용할 수 있습니다.
                          </p>
                          <button 
                            className="ai-search-login-btn"
                            onClick={() => signInWithRedirect()}
                          >
                            로그인하기
                          </button>
                        </div>
                      ) : (
                        <>
                          <form onSubmit={handleSearchSubmit} className="ai-search-form">
                            <div className="ai-search-input-wrapper">
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="워크플로우 개발에 대한 궁금한 점을 검색하세요..."
                                className="ai-search-input"
                                disabled={isSearching || searchLimitReached}
                              />
                              <button 
                                type="submit" 
                                className="ai-search-btn"
                                disabled={!searchQuery.trim() || isSearching || searchLimitReached}
                              >
                                {isSearching ? <Icon icon="mdi:loading" width={18} height={18} className="spin-icon" /> : <Icon icon="mdi:magnify" width={18} height={18} />}
                              </button>
                            </div>
                          </form>
                          
                          {/* 검색 결과 표시 */}
                          {isSearching && (
                            <div className="ai-search-loading">
                              <div className="loading-spinner"></div>
                              <p>AI가 검색 중입니다...</p>
                            </div>
                          )}
                          
                          {searchError && (
                            <div className="ai-search-error">
                              <p><Icon icon="mdi:close-circle-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />{searchError}</p>
                            </div>
                          )}
                          
                          {searchLimitReached && !isSearching && (
                            <div className="ai-search-limit-message">
                              <p><Icon icon="mdi:alert-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />하루 최대 {MAX_SEARCHES_PER_DAY}회 검색 제한에 도달했습니다. 내일 다시 시도해주세요.</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : isAuthenticated === false ? (
                <div className="process-detail-button-container">
                  <p className="process-detail-notice">
                    로그인하시면 상세한 내용을 확인할 수 있습니다.
                  </p>
                  <button 
                    className="btn-show-process-detail"
                    onClick={() => signInWithRedirect()}
                  >
                    로그인하기
                  </button>
                </div>
              ) : id === '7' && isAuthenticated === true ? (
                <div className="guide-content">
                  {/* 시스템 개요 */}
                  <div className="guide-section guide-overview">
                    <h3>시스템 개요</h3>
                    <p>이 워크플로우는 n8n을 통해 매일 최신 뉴스를 자동으로 수집하고, <strong>Perplexity API로 뉴스 검색 → GPT-4.1로 요약 분석 → Gemini로 썸네일 이미지 생성 → Airtable 저장</strong>까지 완전 자동화된 데이터 파이프라인을 구축합니다.</p>
                    
                    <div className="guide-features">
                      <h4>핵심 기능</h4>
                      <ul className="tips-list">
                        <li>Perplexity API를 이용한 뉴스 헤드라인 크롤링</li>
                        <li>GPT-4.1 기반 뉴스 요약 생성</li>
                        <li>Gemini 이미지 모델을 통한 관련 썸네일 자동 생성</li>
                        <li>Airtable DB에 뉴스 정보와 생성 이미지 저장</li>
                      </ul>
                    </div>
                  </div>

                  {/* 사전 준비사항 */}
                  <div className="guide-section guide-requirements">
                    <h3>사전 준비사항</h3>
                    
                    <div className="guide-step">
                      <div className="guide-step-number">1</div>
                      <div className="guide-step-content">
                        <h4>n8n 버전</h4>
                        <p>최신 n8n 버전(≥ 1.58.0) 권장</p>
                        <p>Docker 또는 클라우드 환경 모두 지원</p>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">2</div>
                      <div className="guide-step-content">
                        <h4>필수 API Key</h4>
                        <div className="guide-api-table">
                          <table>
                            <thead>
                              <tr>
                                <th>서비스</th>
                                <th>용도</th>
                                <th>등록 위치</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td><strong>Perplexity API</strong></td>
                                <td>뉴스 검색</td>
                                <td>Credentials: <code>perplexityApi</code></td>
                              </tr>
                              <tr>
                                <td><strong>OpenAI GPT-4.1</strong></td>
                                <td>뉴스 요약</td>
                                <td>Credentials: <code>openAiApi</code></td>
                              </tr>
                              <tr>
                                <td><strong>Google Gemini 2.5 Flash Image</strong></td>
                                <td>이미지 생성</td>
                                <td>Credentials: <code>googlePalmApi</code></td>
                              </tr>
                              <tr>
                                <td><strong>Airtable API Token</strong></td>
                                <td>DB 저장</td>
                                <td>Credentials: <code>airtableTokenApi</code></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">3</div>
                      <div className="guide-step-content">
                        <h4>환경 변수 설정</h4>
                        <div className="guide-code">
                          <code>{`OPENAI_API_KEY=sk-xxxxxx
PERPLEXITY_API_KEY=pplx-xxxxxx
AIRTABLE_TOKEN=pat-xxxxxx
GEMINI_API_KEY=gai-xxxxxx`}</code>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 워크플로우 개요 */}
                  <div className="guide-section">
                    <h3>워크플로우 개요</h3>
                    <p>이 워크플로우는 총 <strong>9개 노드</strong>로 구성되어 있으며, 다음 순서로 실행됩니다:</p>
                    
                    <div className="guide-node-list">
                      <ol className="tips-list" style={{ paddingLeft: '20px' }}>
                        <li><strong>Schedule Trigger</strong> — 자동 실행 스케줄 지정 (예: 매일 9시)</li>
                        <li><strong>news_base</strong> — Airtable에서 검색 기준(<code>query</code>, <code>systemPrompt</code>) 불러오기</li>
                        <li><strong>settings</strong> — Airtable에서 불러온 데이터 쿼리를 파싱 및 전처리</li>
                        <li><strong>HTTP Request</strong> — Perplexity API로 최신 뉴스 데이터 요청</li>
                        <li><strong>Split Out</strong> — 10개 이상의 결과를 개별 뉴스 단위로 분리</li>
                        <li><strong>Edit Fields2</strong> — 뉴스 제목, 본문, 링크, 날짜 필드 정리</li>
                        <li><strong>Message a model (GPT-4.1)</strong> — 뉴스 요약 및 분류 생성</li>
                        <li><strong>Create a record (Airtable)</strong> — 결과를 Airtable에 저장</li>
                        <li><strong>(선택) 이미지 생성 파이프라인</strong> — 뉴스 요약 기반 이미지 생성 후 DB 업데이트</li>
                      </ol>
                    </div>
                  </div>

                  {/* 노드 구성 상세 */}
                  <div className="guide-section">
                    <h3>노드 구성 상세</h3>
                    
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Schedule Trigger</h4>
                          <p><strong>역할:</strong> 정해진 주기(예: 매일 오전 9시)에 워크플로우 자동 실행</p>
                          <p><strong>설정:</strong> <code>Every 1 day</code>, <code>At 09:00</code></p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>news_base (Airtable)</h4>
                          <p><strong>Operation:</strong> <code>Search Record</code></p>
                          <p><strong>Table:</strong> <code>news_base</code> (CrawlingDB 내)</p>
                          <p><strong>Filter Formula:</strong> <code>case = 'news1'</code></p>
                          <p><strong>출력 필드:</strong> <code>case</code>, <code>query</code>, <code>systemPrompt</code></p>
                          <p><strong>기능:</strong> Perplexity API 요청에 사용할 검색어 및 시스템 프롬프트 불러오기</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">3</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>settings</h4>
                          <p><strong>역할:</strong> Airtable에서 불러온 <code>query</code> 문자열을 배열로 변환</p>
                          <p><strong>코드 예시:</strong></p>
                          <div className="guide-code">
                            <code>{`$json.query.split(',').map(s => s.trim()).filter(Boolean)`}</code>
                          </div>
                          <p><strong>출력 예시:</strong></p>
                          <div className="guide-code">
                            <code>{`{ "queries": ["AI", "테크 뉴스", "경제 이슈"] }`}</code>
                          </div>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">4</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>HTTP Request (Perplexity)</h4>
                          <p><strong>Method:</strong> POST</p>
                          <p><strong>URL:</strong> <code>https://api.perplexity.ai/search</code></p>
                          <p><strong>Body 예시:</strong></p>
                          <div className="guide-code">
                            <code>{`{
  "query": "{{ $json.queries }}",
  "max_results": 10,
  "search_recency_filter": "day",
  "max_tokens_per_page": 2048
}`}</code>
                          </div>
                          <p><strong>출력 필드:</strong> <code>results.title</code>, <code>results.url</code>, <code>results.snippet</code>, <code>results.last_updated</code></p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">5</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Split Out</h4>
                          <p><strong>역할:</strong> <code>results</code> 배열을 각각의 뉴스 아이템으로 분리</p>
                          <p><strong>Output 예시:</strong></p>
                          <div className="guide-code">
                            <code>{`{
  "title": "AI 시장 급성장",
  "url": "https://news.site/article123",
  "snippet": "최근 AI 시장이 빠르게 성장 중이다.",
  "last_updated": "2025-11-02T08:00Z"
}`}</code>
                          </div>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">6</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Edit Fields2</h4>
                          <p><strong>역할:</strong> 뉴스 데이터 필드 매핑</p>
                          <p><strong>출력 필드:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><code>fields['제목']</code>: <code>results.title</code></li>
                            <li><code>link</code>: <code>results.url</code></li>
                            <li><code>content</code>: <code>results.snippet</code></li>
                            <li><code>pubDate</code>: <code>results.last_updated</code></li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">7</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Message a model (GPT-4.1)</h4>
                          <p><strong>모델:</strong> <code>GPT-4.1</code></p>
                          <p><strong>입력:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><code>system</code>: <code>$('news_base').item.json.systemPrompt</code></li>
                            <li><code>user</code>: Edit Field2에서 전달 받은 뉴스 본문 내용("content")</li>
                          </ul>
                          <p><strong>System Prompt:</strong></p>
                          <div className="guide-code">
                            <code>{`다음 입력 받은 변수들을 사용하여, 아래의 지시사항을 따르는 JSON 객체를 생성해줘.

### 입력 내용

title: 
url:
content:
date: 

### 지시사항

- content를 자연스러운 한국어로 번역해줘.
- content의 내용을 바탕으로 1문장의 title 을 설정해줘. 
- summary는 번역된 content를 한국어로 2줄 요약해줘.
- content는 그 해당 내용을 검색해서 한국어로 상세하게 3~4개의 문단으로 분석 내용을 작성해줘.
- time_relative입력 받은  news가 생성된 시간 date를 today 시간과 비교하여  상대적인 시간(예: "1일전", "2시간전", "방금 전")을 계산해서 입력해줘.

    예시) today : 2025년 11월 1일, 
           news date : 2025년 10월 30일 일 경우
            --> "2일전" 으로 표기  

- created time = 입력 받은 date 그대로 

tag : 번역된 content의 내용을 분석하여 다음 예시중 가장 관련성이 높은 주제 태그 1개 (예시 태그: "기술", "성공사례", "비즈니스", "자동화", "창업", "정책", 마케팅)
모든 결과를 반드시 아래에 명시된 출력 형식 (JSON)과 동일한 구조의 JSON 객체로 출력해줘.

### 출력형식(JSON)

{  "title": "여기에 번역된 제목을 입력",
  "content": "여기에 번역된 전체 내용을 입력",
  "summary": "여기에 content의 한국어 2줄 요약 내용을 입력",
  "url": "{{ $json.link }}",
  "time_relative": "여기에 계산된 상대 시간 입력 (예: '1일전')",
  "created time" : "{{ $json.pubDate }}",
  "tags": ["태그"]
}

- 출력형식 이외의 다른 내용은 절대로 추가하지 말 것.`}</code>
                          </div>
                          <p><strong>출력 예시:</strong></p>
                          <div className="guide-code">
                            <code>{`{
  "message": {
    "content": {
      "title": "AI 시장 급성장, 글로벌 투자 가속",
      "summary": "최근 AI 산업이 급속히 성장하며 주요 기업들이 투자 경쟁에 나서고 있다.",
      "url": "https://news.site/article123",
      "category": "AI / 경제",
      "time_relative": "2시간 전",
      "created time": "2025-11-02T09:30Z"
    }
  }
}`}</code>
                          </div>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">8</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Create a record (Airtable)</h4>
                          <p><strong>Operation:</strong> <code>Create Record</code></p>
                          <p><strong>Base:</strong> <code>CrawlingDB(자신의 데이터 저장용 DB이름)</code></p>
                          <p><strong>Table:</strong> <code>NewsData (자산의 데이터 저장용 테이블 이름)</code></p>
                          <p><strong>필드 매핑:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><code>title(제목) </code>: <code>{`{{ $json.message.content.title }}`}</code></li>
                            <li><code>content(뉴스내용)</code>: <code>{`{{ $json.message.content.content }}`}</code></li>
                            <li><code>url(뉴스 링크)</code>: <code>{`{{ $json.message.content.url }}`}</code></li>
                            <li><code>summary(뉴스 요약)</code>: <code>{`{{ $json.message.content.summary }}`}</code></li>
                            <li><code>category(뉴스 카테고리)</code>: <code>{`{{ $json.message.content.tags[0] }}`}</code></li>
                            <li><code>created(뉴스 생성 시간)</code>: <code>{`{{ $json.message.content['created time'] }}`}</code></li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">9</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>(선택) 이미지 생성 파이프라인</h4>
                          <p>이 단계는 뉴스 요약 결과를 기반으로 <strong>Gemini 이미지 모델</strong>을 활용하여 썸네일 이미지를 자동 생성하고 Airtable에 업데이트합니다.</p>
                          <p><strong>구성 흐름:</strong></p>
                          <p><code>Message a model1 (GPT-4.1 프롬프트 생성)</code> → <code>Generate an image (Gemini)</code> → <code>Update record (Airtable)</code></p>
                          <p><strong>출력 이미지 모델:</strong> <code>gemini-2.5-flash-image</code></p>
                          <p><strong>Negative Prompt 규칙:</strong> "Avoid distorted fingers, broken text, or unwanted artifacts."</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Airtable 연동 */}
                  <div className="guide-section guide-airtable-info">
                    <h3>Airtable 연동</h3>
                    <ul className="tips-list">
                      <li><strong>Database:</strong> <code>CrawlingDB</code></li>
                      <li><strong>Tables:</strong>
                        <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
                          <li><code>news_base</code> — 검색어/프롬프트 관리</li>
                          <li><code>NewsData</code> — 결과 저장 (뉴스 요약 + 이미지)</li>
                        </ul>
                      </li>
                      <li><strong>URL 예시:</strong>
                        <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
                          <li><code>https://airtable.com/appdNkqN4a****</code></li>
                          <li><code>https://airtable.com/appdNkqN4apcQlNxp/tblNVjvaD82****</code></li>
                        </ul>
                      </li>
                    </ul>
                  </div>

                  {/* 자동화 스케줄 설정 */}
                  <div className="guide-section">
                    <h3>자동화 스케줄 설정</h3>
                    <p>매일 오전 09:00 뉴스 데이터 자동 크롤링</p>
                    <p>필요 시 n8n Cron 설정:</p>
                    <div className="guide-code">
                      <code>0 9 * * *</code>
                    </div>
                  </div>

                  {/* 주의사항 및 팁 */}
                  <div className="guide-section guide-warnings">
                    <h3>주의사항 및 팁</h3>
                    <div className="guide-tips">
                      <h4>1. API 한도 관리</h4>
                      <ul className="tips-list">
                        <li>Perplexity: 초당 1회, 하루 1,000회 이내 권장</li>
                        <li>OpenAI / Gemini 호출 시 요금 부과 주의</li>
                      </ul>
                      
                      <h4>2. Airtable Rate Limit</h4>
                      <ul className="tips-list">
                        <li>초당 5회 이하 요청 유지</li>
                      </ul>
                      
                      <h4>3. 타임아웃 설정</h4>
                      <ul className="tips-list">
                        <li>HTTP Request: Timeout 60초 이상</li>
                        <li>모델 응답 지연 시 <code>retry on fail</code> 옵션 추가</li>
                      </ul>
                      
                      <h4>4. 데이터 품질 개선</h4>
                      <ul className="tips-list">
                        <li><code>systemPrompt</code>를 Airtable에서 자유롭게 조정 가능</li>
                        <li>예: "한국 테크 뉴스만 요약해줘" / "AI 관련 기사만 분석해줘"</li>
                      </ul>
                    </div>
                  </div>

                  {/* 확장 아이디어 */}
                  <div className="guide-section guide-resources">
                    <h3>확장 아이디어</h3>
                    <ul className="tips-list">
                      <li>Slack / Discord 연동으로 뉴스 요약 자동 알림</li>
                      <li>Notion에 일일 리포트 자동 작성</li>
                      <li>요약문 기반 감성 분석 결과 추가</li>
                      <li>월간 뉴스 트렌드 리포트 자동 생성</li>
                    </ul>
                  </div>

                  {/* AI 검색 영역 */}
                  <div className="ai-search-section">
                    <div className="ai-search-header">
                      <h3 className="ai-search-title">AI 검색</h3>
                      <p className="ai-search-description">
                        워크플로우 개발에 대한 궁금한 점을 검색해보세요. RAG 기반 AI가 관련 정보를 제공합니다.
                      </p>
                      {isAuthenticated === true && (
                        <p className="ai-search-limit-info">
                          하루 {MAX_SEARCHES_PER_DAY}회 제한 (오늘 {searchCount}/{MAX_SEARCHES_PER_DAY}회 사용)
                        </p>
                      )}
                    </div>
                    
                    <div className="ai-search-container">
                      {isAuthenticated !== true ? (
                        <div className="ai-search-login-required">
                          <p className="ai-search-login-message">
                            <Icon icon="mdi:lock-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> 로그인이 필요합니다. 로그인 후 AI 검색을 사용할 수 있습니다.
                          </p>
                          <button 
                            className="ai-search-login-btn"
                            onClick={() => signInWithRedirect()}
                          >
                            로그인하기
                          </button>
                        </div>
                      ) : (
                        <>
                          <form onSubmit={handleSearchSubmit} className="ai-search-form">
                            <div className="ai-search-input-wrapper">
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="워크플로우 개발에 대한 궁금한 점을 검색하세요..."
                                className="ai-search-input"
                                disabled={isSearching || searchLimitReached}
                              />
                              <button 
                                type="submit" 
                                className="ai-search-btn"
                                disabled={!searchQuery.trim() || isSearching || searchLimitReached}
                              >
                                {isSearching ? <Icon icon="mdi:loading" width={18} height={18} className="spin-icon" /> : <Icon icon="mdi:magnify" width={18} height={18} />}
                              </button>
                            </div>
                          </form>
                          
                          {/* 검색 결과 표시 */}
                          {isSearching && (
                            <div className="ai-search-loading">
                              <div className="loading-spinner"></div>
                              <p>AI가 검색 중입니다...</p>
                            </div>
                          )}
                          
                          {searchError && (
                            <div className="ai-search-error">
                              <p><Icon icon="mdi:close-circle-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />{searchError}</p>
                            </div>
                          )}
                          
                          {searchLimitReached && !isSearching && (
                            <div className="ai-search-limit-message">
                              <p><Icon icon="mdi:alert-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />하루 최대 {MAX_SEARCHES_PER_DAY}회 검색 제한에 도달했습니다. 내일 다시 시도해주세요.</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : id === '8' && isAuthenticated === true ? (
                <div className="guide-content">
                  {/* 시스템 개요 */}
                  <div className="guide-section guide-overview">
                    <h3>시스템 개요</h3>
                    <p>n8n과 Google Gemini(PaLM) API를 연동하여, 해외 기술 유튜버들의 영상을 주기적으로 모니터링하고, <strong>스크립트를 추출해 학습 가이드 및 비즈니스 아이디어로 변환</strong>한 뒤 Airtable에 자동으로 저장하는 AI 에이전트 시스템 구축 가이드입니다.</p>
                    
                    <div className="guide-features">
                      <h4>핵심 기능</h4>
                      <ul className="tips-list">
                        <li><strong>모니터링:</strong> 지정된 YouTube 채널의 RSS 피드를 매시간 확인하여 신규 영상을 감지합니다.</li>
                        <li><strong>필터링:</strong> Shorts 영상 및 라이브 스트리밍 영상을 제외하고 정규 영상만 선별합니다.</li>
                        <li><strong>스크립트 추출:</strong> Python 스크립트를 실행하여 해당 영상의 자막(Transcript)을 추출합니다.</li>
                        <li><strong>AI 분석:</strong> Google Gemini 모델을 통해 스크립트를 분석하여 '한줄 요약', '상세 학습 가이드', '비즈니스 활용 방안'을 도출합니다.</li>
                        <li><strong>아카이빙:</strong> 분석된 데이터를 Airtable 데이터베이스에 구조화하여 저장합니다.</li>
                      </ul>
                    </div>
                  </div>

                  {/* 사전 준비사항 */}
                  <div className="guide-section guide-requirements">
                    <h3>사전 준비사항</h3>
                    
                    <div className="guide-step">
                      <div className="guide-step-number">1</div>
                      <div className="guide-step-content">
                        <h4>n8n 버전 및 환경</h4>
                        <p><strong>Self-hosted n8n 권장:</strong> 로컬 Python 스크립트 실행 노드(`Execute Command`)를 사용하므로, n8n이 설치된 서버(Docker 컨테이너 등) 내에 Python 환경과 스크립트 파일이 구성되어 있어야 합니다.</p>
                        <p><strong>Python 라이브러리:</strong> <code>youtube-transcript-api</code> 등 자막 추출을 위한 라이브러리 설치 필요.</p>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">2</div>
                      <div className="guide-step-content">
                        <h4>필수 API Key</h4>
                        <div className="guide-api-table">
                          <table>
                            <thead>
                              <tr>
                                <th>서비스</th>
                                <th>용도</th>
                                <th>등록 위치</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td><strong>Google Gemini PaLM API</strong></td>
                                <td>AI 분석 및 학습 가이드 생성</td>
                                <td>Credentials: <code>googlePalmApi</code></td>
                              </tr>
                              <tr>
                                <td><strong>YouTube Data API</strong></td>
                                <td>영상 정보 조회 및 라이브 체크</td>
                                <td>Credentials: <code>youtubeApi</code></td>
                              </tr>
                              <tr>
                                <td><strong>Airtable Personal Access Token</strong></td>
                                <td>분석 결과 저장</td>
                                <td>Credentials: <code>airtableTokenApi</code></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p style={{ marginTop: '15px' }}><strong>필요한 정보:</strong></p>
                        <ul className="tips-list" style={{ paddingLeft: '20px', marginTop: '10px' }}>
                          <li>Airtable Base ID (자동화 youtube 분석)</li>
                          <li>Airtable Table ID (글로벌 n8n 자동화)</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* 에이전트 구축 상세 */}
                  <div className="guide-section">
                    <h3>에이전트 구축 상세</h3>
                    
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>트리거 및 영상 필터링 (RSS & Filtering)</h4>
                          <p><strong>RSS Feed Trigger:</strong> NetworkChuck, Two Minute Papers 등 주요 채널의 피드를 매시간 폴링합니다.</p>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Shorts 필터링 (If 노드)</h4>
                          <p>URL에 <code>shorts</code>가 포함된 경우 프로세스를 중단합니다.</p>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>데이터 정규화 (Set 노드)</h4>
                          <p>Title, Link, Author, Video ID, Date 등을 표준 변수로 정리합니다.</p>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Youtube API & 라이브 체크</h4>
                          <p><strong>YouTube 노드:</strong> 영상의 상세 정보(Duration)를 조회합니다.</p>
                          <p><strong>Code 노드 (Javascript):</strong> ISO Duration을 초(Seconds) 단위로 변환하고 <code>isLive</code> 상태를 판별합니다.</p>
                          <p><strong>Live 필터링 (If 노드):</strong> 실시간 스트리밍 영상(<code>isLive: true</code>)은 제외합니다.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>스크립트 추출 (Script Extraction)</h4>
                          <p><strong>Execute Command 노드:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li><strong>Command:</strong> <code>{`python3 /home/node/.n8n/youtube_test.py {{ $('youtube_info').item.json.id }}`}</code></li>
                            <li>서버 내부의 Python 스크립트를 호출하여 영상 ID에 해당하는 자막 텍스트를 <code>stdout</code>으로 반환받습니다.</li>
                          </ul>
                          <p><strong>Python 스크립트 예시:</strong></p>
                          <div className="guide-code">
                            <code>{`from youtube_transcript_api import YouTubeTranscriptApi

video_id = sys.argv[1]
transcript = YouTubeTranscriptApi.get_transcript(video_id)
text = ' '.join([item['text'] for item in transcript])
print(text)`}</code>
                          </div>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">3</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>AI 분석 및 저장 (AI Analysis & Storage)</h4>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Google Gemini (AI Agent)</h4>
                          <p><strong>Model:</strong> <code>models/gemini-2.5-pro</code> (또는 최신 Gemini Pro 모델)</p>
                          <p><strong>Input:</strong> Python 스크립트에서 추출한 자막 텍스트(<code>{`{{ $json.stdout }}`}</code>)</p>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Data Parsing (Set 노드)</h4>
                          <p>AI가 출력한 JSON 문자열을 n8n 객체로 파싱합니다 (<code>JSON.parse(...)</code>).</p>
                          
                          <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Airtable (Create Record)</h4>
                          <p>분석 결과를 각 컬럼에 매핑하여 저장합니다.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">4</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>유튜브 분석 전용 프롬프트</h4>
                          <p><strong>시스템 프롬프트 (System Message)</strong></p>
                          <p>Google Gemini 노드에 설정된 시스템 지침입니다. AI가 응답을 JSON 형식으로 강제하도록 설계되었습니다.</p>
                          
                          <div className="guide-code">
                            <code>{`# 역할
당신은 유튜브 영상 스크립트를 분석하여, 해당 내용을 **학습하기 위한 상세 가이드**와 **비즈니스에 적용할 수 있는 활용 방안**을 Slack 메시지에 최적화된 Block Kit JSON 형식으로 생성하는 전문 AI입니다.

# 지시사항
제공되는 유튜브 영상 스크립트를 바탕으로, 아래의 **'생성 원칙'**과 **'출력 JSON 구조'**를 엄격히 준수하여 유효한 JSON 데이터만을 한국어로 생성해 주세요.

# 생성 원칙
1. **"상세가이드" 구성:**
   * **실행 가능성:** 영상의 내용을 사용자가 실제로 익히고 따라 할 수 있도록 구체적이고 단계적인 가이드를 제공합니다.
   * **핵심 집중:** 단순 요약이 아닌, 영상의 핵심 기술이나 지식을 습득하는 데 초점을 맞춥니다.

2. **"비즈니스활용" 구성:**
   * **가치 제안:** 영상의 주제(특히 새로운 자동화 기능, 기술, 마케팅 전략 등)가 비즈니스에 어떤 가치(예: 효율성 증대, 비용 절감, 새로운 수익 창출)를 제공하는지 명확히 합니다.
   * **구체적 시나리오:** '마케팅에 활용'과 같은 추상적인 내용이 아닌, '신규 고객 온보딩 프로세스 자동화에 적용'처럼 구체적인 활용 사례와 아이디어들(3~4개)을 제시합니다.

# 출력 JSON 구조
반드시 아래와 같은 Slack Block Kit JSON 형식을 따라야 합니다. []로 표시된 부분에 분석 및 생성 내용을 채워 넣으세요.
{
  "한줄요약" : "[영상 전체 내용을 한 문장으로 압축 요약]",
  "상세가이드" : "[학습/실행을 위한 단계별 상세한 가이드]",
  "비즈니스활용" : "[업무/비즈니스에 적용할 수 있는 다양한 아이디어들]"
}

# JSON 생성 규칙
* **JSON만 출력:** 다른 설명이나 인사말 없이, 반드시 유효한 JSON 객체만을 출력해야 합니다.`}</code>
                          </div>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">5</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>데이터 저장 구조 (Airtable Mapping)</h4>
                          <p>AI 분석 결과는 최종적으로 Airtable에 아래와 같이 매핑되어 저장됩니다.</p>
                          <div className="guide-api-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Airtable 컬럼명</th>
                                  <th>매핑된 값 (Expression)</th>
                                  <th>설명</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td><strong>youtube_name</strong></td>
                                  <td><code>{`{{ $('full_video check').item.json.author }}`}</code></td>
                                  <td>유튜브 채널명</td>
                                </tr>
                                <tr>
                                  <td><strong>작성일</strong></td>
                                  <td><code>{`{{ $('full_video check').item.json.pubDate }}`}</code></td>
                                  <td>영상 게시일</td>
                                </tr>
                                <tr>
                                  <td><strong>한줄요약</strong></td>
                                  <td><code>{`{{ $json.data['한줄요약'] }}`}</code></td>
                                  <td>AI가 생성한 핵심 요약</td>
                                </tr>
                                <tr>
                                  <td><strong>상세가이드</strong></td>
                                  <td><code>{`{{ $json.data['상세가이드'] }}`}</code></td>
                                  <td>학습 및 실행 가이드</td>
                                </tr>
                                <tr>
                                  <td><strong>비즈니스 가이드</strong></td>
                                  <td><code>{`{{ $json.data['비즈니스활용'] }}`}</code></td>
                                  <td>비즈니스 아이디어</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 주의사항 및 팁 */}
                  <div className="guide-section guide-warnings">
                    <h3>주의사항 및 팁</h3>
                    <div className="guide-tips">
                      <h4>1. Python 환경 구성 필수</h4>
                      <p>이 워크플로우는 <code>Execute Command</code> 노드를 통해 로컬 경로(<code>/home/node/.n8n/youtube_test.py</code>)의 파일을 실행합니다. 따라서 n8n이 실행되는 컨테이너나 서버에 Python과 해당 스크립트 파일이 반드시 존재해야 작동합니다.</p>
                      
                      <h4 style={{ marginTop: '15px', fontSize: '1rem' }}>Python 스크립트 설치:</h4>
                      <div className="guide-code">
                        <code>{`pip install youtube-transcript-api`}</code>
                      </div>
                      
                      <h4>2. 자막(Transcript) 유무</h4>
                      <p>자막이 제공되지 않는 영상의 경우 Python 스크립트 단계에서 에러가 발생할 수 있습니다. <code>Execute Command</code> 노드 설정에서 "Continue On Fail" 옵션을 켜두거나, 에러 핸들링 노드를 추가하는 것을 권장합니다.</p>
                      
                      <h4>3. JSON 파싱 오류 방지</h4>
                      <p>Gemini 모델이 가끔 JSON 앞뒤에 Markdown 코드 블록(<code>\`\`\`json ... \`\`\`</code>)을 붙이는 경우가 있습니다. 이를 방지하기 위해 <code>Set</code> 노드에서 <code>.slice(8,-4)</code>와 같은 문자열 처리를 수행하고 있으나, 모델 업데이트에 따라 정규식(Regex)을 사용하여 더 견고하게 JSON만 추출하도록 수정하는 것이 좋습니다.</p>
                      
                      <h4>4. API 할당량(Quota) 관리</h4>
                      <p>RSS 폴링 주기가 너무 짧거나 구독 채널이 많으면 YouTube API 호출 한도에 도달할 수 있습니다. 적절한 폴링 주기(예: 1시간)를 유지하세요.</p>
                      
                      <h4 style={{ marginTop: '15px', fontSize: '1rem' }}>권장 설정:</h4>
                      <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                        <li>RSS Feed Trigger: 매시간 실행</li>
                        <li>YouTube API 호출: 영상당 1회</li>
                        <li>Gemini API 호출: 영상당 1회</li>
                      </ul>
                    </div>
                  </div>

                  {/* AI 검색 영역 */}
                  <div className="ai-search-section">
                    <div className="ai-search-header">
                      <h3 className="ai-search-title">AI 검색</h3>
                      <p className="ai-search-description">
                        워크플로우 개발에 대한 궁금한 점을 검색해보세요. RAG 기반 AI가 관련 정보를 제공합니다.
                      </p>
                      {isAuthenticated === true && (
                        <p className="ai-search-limit-info">
                          하루 {MAX_SEARCHES_PER_DAY}회 제한 (오늘 {searchCount}/{MAX_SEARCHES_PER_DAY}회 사용)
                        </p>
                      )}
                    </div>
                    
                    <div className="ai-search-container">
                      {isAuthenticated !== true ? (
                        <div className="ai-search-login-required">
                          <p className="ai-search-login-message">
                            <Icon icon="mdi:lock-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> 로그인이 필요합니다. 로그인 후 AI 검색을 사용할 수 있습니다.
                          </p>
                          <button 
                            className="ai-search-login-btn"
                            onClick={() => signInWithRedirect()}
                          >
                            로그인하기
                          </button>
                        </div>
                      ) : (
                        <>
                          <form onSubmit={handleSearchSubmit} className="ai-search-form">
                            <div className="ai-search-input-wrapper">
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="워크플로우 개발에 대한 궁금한 점을 검색하세요..."
                                className="ai-search-input"
                                disabled={isSearching || searchLimitReached}
                              />
                              <button 
                                type="submit" 
                                className="ai-search-btn"
                                disabled={!searchQuery.trim() || isSearching || searchLimitReached}
                              >
                                {isSearching ? <Icon icon="mdi:loading" width={18} height={18} className="spin-icon" /> : <Icon icon="mdi:magnify" width={18} height={18} />}
                              </button>
                            </div>
                          </form>
                          
                          {/* 검색 결과 표시 */}
                          {isSearching && (
                            <div className="ai-search-loading">
                              <div className="loading-spinner"></div>
                              <p>AI가 검색 중입니다...</p>
                            </div>
                          )}
                          
                          {searchError && (
                            <div className="ai-search-error">
                              <p><Icon icon="mdi:close-circle-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />{searchError}</p>
                            </div>
                          )}
                          
                          {searchLimitReached && !isSearching && (
                            <div className="ai-search-limit-message">
                              <p><Icon icon="mdi:alert-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />하루 최대 {MAX_SEARCHES_PER_DAY}회 검색 제한에 도달했습니다. 내일 다시 시도해주세요.</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : id === '4' && isAuthenticated === true ? (
                <div className="guide-content">
                  {/* 시스템 개요 */}
                  <div className="guide-section guide-overview">
                    <h3>시스템 개요</h3>
                    <p>n8n과 공공데이터포털(data.go.kr) API를 연동하여, 창업진흥원, 중소벤처기업부, 과학기술정보통신부의 사업 공고를 자동으로 수집하고, 데이터 표준화 과정을 거쳐 Airtable 데이터베이스에 저장하는 자동화 시스템 구축 가이드입니다.</p>
                    
                    <div className="guide-features">
                      <h4>핵심 기능</h4>
                      <ul className="tips-list">
                        <li><strong>정부 지원사업 통합 크롤러:</strong> 서로 다른 데이터 구조(XML)를 가진 3개 정부 기관의 API를 호출하여 JSON으로 변환, 필드를 통일(Normalization)한 후 중복 없이 Airtable에 업서트(Upsert)합니다.</li>
                        <li><strong>병렬 처리 구조:</strong> 3개의 API 요청이 동시에 시작되어 효율적인 데이터 수집을 수행합니다.</li>
                        <li><strong>데이터 표준화:</strong> 각 기관별로 다른 필드명을 Airtable 스키마에 맞춰 통일합니다.</li>
                      </ul>
                    </div>
                  </div>

                  {/* 사전 준비사항 */}
                  <div className="guide-section guide-requirements">
                    <h3>사전 준비사항</h3>
                    
                    <div className="guide-step">
                      <div className="guide-step-number">1</div>
                      <div className="guide-step-content">
                        <h4>n8n 버전 및 환경</h4>
                        <p>최신 버전의 n8n (HTTP Request 및 Code 노드 활용)</p>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">2</div>
                      <div className="guide-step-content">
                        <h4>API 키 준비</h4>
                        <p><strong>보안을 위해 ID 및 키 값의 일부는 마스킹 처리되었습니다.</strong></p>
                        <ul className="tips-list" style={{ marginTop: '10px' }}>
                          <li><strong>공공데이터포털 Service Key:</strong> <code>627***</code> (HTTP Request 노드의 Query Parameter로 사용)</li>
                          <li><strong>Airtable Token (Credential):</strong> <code>kI1***</code> (FlowTo.ai Airtable)</li>
                          <li><strong>Airtable Base Info:</strong>
                            <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                              <li>Base ID: <code>app***</code></li>
                              <li>Table ID: <code>tbl***</code></li>
                            </ul>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* 워크플로우 구축 상세 */}
                  <div className="guide-section guide-steps">
                    <h3>워크플로우 구축 상세</h3>
                    <p style={{ marginBottom: '20px' }}><em>(참고: 제공된 이미지 파일 `정부과제 크롤링.png` 기준)</em></p>

                    <div className="guide-step">
                      <div className="guide-step-number">1</div>
                      <div className="guide-step-content guide-node-detail">
                        <h4>기본 노드 구성 및 데이터 수집</h4>
                        <p>이 워크플로우는 병렬 처리 구조를 가지고 있습니다. Trigger 실행 시 3개의 API 요청이 동시에 시작됩니다.</p>
                        
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Trigger (Manual)</h4>
                        <p>워크플로우 수동 실행 트리거</p>
                        
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>HTTP Requests (Get Method)</h4>
                        <ol className="tips-list" style={{ paddingLeft: '20px' }}>
                          <li><strong>창업진흥원:</strong> <code>https://apis.data.go.kr/.../getAnnouncementInformation01</code></li>
                          <li><strong>중소벤처기업부:</strong> <code>https://apis.data.go.kr/.../getbizList_v2</code></li>
                          <li><strong>과학기술정보통신부:</strong> <code>https://apis.data.go.kr/.../businessAnnouncMentList</code></li>
                        </ol>
                        <p style={{ marginTop: '10px' }}><em>* 공통 설정: Query Parameter에 <code>serviceKey</code>와 <code>numOfRows</code>(또는 <code>perPage</code>) 설정</em></p>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">2</div>
                      <div className="guide-step-content guide-node-detail">
                        <h4>데이터 변환 및 전처리 (Logic & Code)</h4>
                        <p>각 기관마다 데이터 구조가 다르므로, 개별적인 후처리 로직이 적용됩니다.</p>
                        
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>A. 창업진흥원 (K-startup) 처리</h4>
                        <p>K-startup 데이터는 구조가 복잡하여 XML 변환 후 JavaScript로 직접 파싱합니다.</p>
                        <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                          <li><strong>XML Node:</strong> XML 응답을 JSON으로 1차 변환</li>
                          <li><strong>Code Node (JavaScript):</strong> 중첩된 배열 구조 평탄화 및 텍스트 정제</li>
                        </ul>
                        
                        <div className="guide-code" style={{ marginTop: '15px' }}>
                          <code>{`// 1. 입력 데이터를 가져오고 공고 목록이 포함된 배열 경로를 찾습니다.
const inputData = $input.all();
const rawResults = inputData[0].json.results;
const items = rawResults?.data?.item;

// 항목이 없으면 빈 배열 반환
if (!items || !Array.isArray(items)) {
    return [];
}

const outputItems = [];

// 2. 각 공고 항목 순회 및 평탄화(Flatten)
for (const item of items) {
    const newItem = {};
    const colArray = item.col; // K-startup 특유의 데이터 구조 (col 배열)

    if (colArray && Array.isArray(colArray)) {
        for (const col of colArray) {
            const key = col['name'];
            let value = col['_'] !== undefined ? col['_'] : ''; 

            // 공고 내용 줄바꿈 문자열 제거 및 정제
            if (key === 'pbanc_ctnt' && typeof value === 'string') {
                value = value.replace(/\\r\\n/g, ' ').trim();
            }
            if (typeof value === 'string') {
                value = value.replace(/&amp;/g, '&');
            }
            if (key) {
                newItem[key] = value;
            }
        }
    }
    outputItems.push({ json: newItem });
}
return outputItems;`}</code>
                        </div>

                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>B. 중소벤처기업부 & 과학기술정보통신부 처리</h4>
                        <p>비교적 표준화된 XML 구조를 가지므로 n8n 내장 노드를 사용합니다.</p>
                        <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                          <li><strong>XML Node:</strong> XML → JSON 변환</li>
                          <li><strong>Split Out Node:</strong> <code>response.body.items.item</code> 경로를 지정하여 배열을 개별 아이템으로 분리</li>
                        </ul>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">3</div>
                      <div className="guide-step-content guide-node-detail">
                        <h4>데이터 표준화 (Edit Fields)</h4>
                        <p>서로 다른 필드명(예: <code>biz_pbanc_nm</code> vs <code>title</code>)을 Airtable 스키마에 맞춰 통일합니다.</p>
                        
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>매핑 예시 (Airtable 컬럼 기준)</h4>
                        <div className="guide-api-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Airtable 필드</th>
                                <th>창업진흥원 소스</th>
                                <th>중소벤처기업부 소스</th>
                                <th>과기부 소스</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td><strong>site</strong></td>
                                <td>"창업진흥원 k-startup"</td>
                                <td>"중소벤처기업부"</td>
                                <td>"과학기술정보통신부"</td>
                              </tr>
                              <tr>
                                <td><strong>공고명</strong></td>
                                <td><code>{`{{ $json.biz_pbanc_nm }}`}</code></td>
                                <td><code>{`{{ $json.title }}`}</code></td>
                                <td><code>{`{{ $json.subject }}`}</code></td>
                              </tr>
                              <tr>
                                <td><strong>공고일</strong></td>
                                <td><code>{`{{ $json.pbanc_rcpt_bgng_dt }}`}</code></td>
                                <td><code>{`{{ $json.applicationStartDate }}`}</code></td>
                                <td><code>{`{{ $json.pressDt }}`}</code></td>
                              </tr>
                              <tr>
                                <td><strong>공고링크</strong></td>
                                <td><code>{`{{ $json.detl_pg_url }}`}</code></td>
                                <td><code>{`{{ $json.viewUrl }}`}</code></td>
                                <td><code>{`{{ $json.viewUrl }}`}</code></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">4</div>
                      <div className="guide-step-content guide-node-detail">
                        <h4>최종 데이터 적재 (Airtable)</h4>
                        <p>3개의 경로가 하나로 합쳐져 Airtable에 저장됩니다.</p>
                        <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                          <li><strong>Node:</strong> Create or update a record</li>
                          <li><strong>Operation:</strong> <code>Upsert</code> (중복 방지)</li>
                          <li><strong>Matching Column:</strong> <code>공고명</code> (동일한 공고명이 있을 경우 업데이트, 없으면 생성)</li>
                          <li><strong>Mapping:</strong> 표준화된 필드(<code>공고명</code>, <code>site</code>, <code>지원자격</code>, <code>공고링크</code>, <code>마감일</code> 등)를 연결</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* 주의사항 및 팁 */}
                  <div className="guide-section guide-warnings">
                    <h3>주의사항 및 팁</h3>
                    <div className="guide-tips">
                      <h4>1. 데이터 정합성 관리</h4>
                      <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                        <li><strong>Upsert 키 설정:</strong> 현재 <code>공고명</code>을 기준으로 중복을 체크합니다. 공고명이 완전히 동일하지만 내용이 다른 경우가 드물게 발생할 수 있으므로, 가능하다면 <code>공고ID</code>(API에서 제공하는 고유값)를 활용하는 것이 더 안전합니다.</li>
                        <li><strong>날짜 포맷:</strong> API마다 날짜 형식이 <code>YYYYMMDD</code> 또는 <code>YYYY-MM-DD</code>로 다를 수 있습니다. Airtable의 Date 필드 타입에 맞게 포맷팅 함수(<code>formatDate()</code>)를 <code>Edit Fields</code> 노드에 추가하는 것을 권장합니다.</li>
                      </ul>
                      
                      <h4 style={{ marginTop: '20px' }}>2. API 호출 제한</h4>
                      <p>공공데이터포털 API는 일일 트래픽 제한이 있습니다. 테스트 시 과도한 호출을 피하고, 실운영 시 <code>Schedule Trigger</code>를 사용하여 하루 1~2회만 실행되도록 설정하세요.</p>
                      
                      <h4 style={{ marginTop: '20px' }}>3. 에러 핸들링</h4>
                      <p>특정 기관 API 서버가 다운되더라도 전체 워크플로우가 멈추지 않도록, 각 HTTP Request 노드 설정에서 <strong>"Continue On Fail"</strong> 옵션을 활성화하는 것을 추천합니다.</p>
                    </div>
                  </div>

                  {/* AI 검색 영역 */}
                  <div className="ai-search-section">
                    <div className="ai-search-header">
                      <h3 className="ai-search-title">AI 검색</h3>
                      <p className="ai-search-description">
                        워크플로우 개발에 대한 궁금한 점을 검색해보세요. RAG 기반 AI가 관련 정보를 제공합니다.
                      </p>
                      {isAuthenticated === true && (
                        <p className="ai-search-limit-info">
                          하루 {MAX_SEARCHES_PER_DAY}회 제한 (오늘 {searchCount}/{MAX_SEARCHES_PER_DAY}회 사용)
                        </p>
                      )}
                    </div>
                    
                    <div className="ai-search-container">
                      {isAuthenticated !== true ? (
                        <div className="ai-search-login-required">
                          <p className="ai-search-login-message">
                            <Icon icon="mdi:lock-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> 로그인이 필요합니다. 로그인 후 AI 검색을 사용할 수 있습니다.
                          </p>
                          <button 
                            className="ai-search-login-btn"
                            onClick={() => signInWithRedirect()}
                          >
                            로그인하기
                          </button>
                        </div>
                      ) : (
                        <>
                          <form onSubmit={handleSearchSubmit} className="ai-search-form">
                            <div className="ai-search-input-wrapper">
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="워크플로우 개발에 대한 궁금한 점을 검색하세요..."
                                className="ai-search-input"
                                disabled={isSearching || searchLimitReached}
                              />
                              <button 
                                type="submit" 
                                className="ai-search-btn"
                                disabled={!searchQuery.trim() || isSearching || searchLimitReached}
                              >
                                {isSearching ? <Icon icon="mdi:loading" width={18} height={18} className="spin-icon" /> : <Icon icon="mdi:magnify" width={18} height={18} />}
                              </button>
                            </div>
                          </form>
                          
                          {/* 검색 결과 표시 */}
                          {isSearching && (
                            <div className="ai-search-loading">
                              <div className="loading-spinner"></div>
                              <p>AI가 검색 중입니다...</p>
                            </div>
                          )}
                          
                          {searchError && (
                            <div className="ai-search-error">
                              <p><Icon icon="mdi:close-circle-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />{searchError}</p>
                            </div>
                          )}
                          
                          {searchLimitReached && !isSearching && (
                            <div className="ai-search-limit-message">
                              <p><Icon icon="mdi:alert-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />하루 최대 {MAX_SEARCHES_PER_DAY}회 검색 제한에 도달했습니다. 내일 다시 시도해주세요.</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : id === '9' && isAuthenticated === true ? (
                <div className="guide-content">
                  {/* 시스템 개요 */}
                  <div className="guide-section guide-overview">
                    <h3>시스템 개요</h3>
                    <p>이 가이드는 <strong>'학습(Learn) → 실행(Do) → 교육(Teach)'</strong>의 검증된 모델을 따릅니다. AI를 활용하여 100만 달러 수익을 달성할 수 있는 체계적인 3단계 비즈니스 구축 방법을 제시합니다.</p>
                    
                    <div className="guide-features">
                      <h4>핵심 모델</h4>
                      <ul className="tips-list">
                        <li><strong>Phase 1 (Learn):</strong> 핵심 기술 습득 - AI 도구를 활용한 전문성 확보</li>
                        <li><strong>Phase 2 (Do):</strong> 서비스 판매 및 고객 확보 - 학습한 기술을 서비스로 전환</li>
                        <li><strong>Phase 3 (Teach):</strong> 지식 상품화 및 스케일업 - 경험을 교육 상품으로 확장</li>
                      </ul>
                    </div>
                  </div>

                  {/* 사전 준비사항 */}
                  <div className="guide-section guide-requirements">
                    <h3>사전 준비사항</h3>
                    
                    <div className="guide-step">
                      <div className="guide-step-number">1</div>
                      <div className="guide-step-content">
                        <h4>AI 도구 계정 준비</h4>
                        <p>각 Phase에서 사용할 AI 도구들의 계정을 미리 준비하세요. 대부분의 도구는 무료 체험판을 제공하므로 먼저 테스트해보는 것을 권장합니다.</p>
                        <div className="guide-api-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Phase</th>
                                <th>필요한 도구</th>
                                <th>용도</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td><strong>Phase 1</strong></td>
                                <td>ChatGPT, Claude, Lovable, Retell AI, Make.com 중 선택</td>
                                <td>핵심 기술 습득</td>
                              </tr>
                              <tr>
                                <td><strong>Phase 2</strong></td>
                                <td>Blot AI/Poppy AI, Instantly AI, Gamma</td>
                                <td>서비스 판매 및 고객 확보</td>
                              </tr>
                              <tr>
                                <td><strong>Phase 3</strong></td>
                                <td>Skool, Fiducia, HeyGen</td>
                                <td>지식 상품화 및 스케일업</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">2</div>
                      <div className="guide-step-content">
                        <h4>비즈니스 목표 설정</h4>
                        <p>어떤 AI 서비스 분야(교육, 컨설팅, 개발)에 집중할지 결정하고, 목표 수익과 타임라인을 설정하세요. 명확한 목표가 있어야 각 Phase를 효과적으로 진행할 수 있습니다.</p>
                      </div>
                    </div>

                    <div className="guide-step">
                      <div className="guide-step-number">3</div>
                      <div className="guide-step-content">
                        <h4>포트폴리오 구축 계획</h4>
                        <p>Phase 1에서 완성할 포트폴리오 프로젝트를 미리 계획하세요. 실제 고객이나 자신의 업무에 적용 가능한 프로젝트를 선택하는 것이 가장 효과적입니다.</p>
                      </div>
                    </div>
                  </div>

                  {/* Phase 1: 학습 단계 구축 상세 */}
                  <div className="guide-section">
                    <h3>Phase 1: 학습 단계 구축 상세</h3>
                    <p><strong>목표:</strong> 세 가지 AI 서비스(교육, 컨설팅, 개발) 중 하나를 선택하여 시장에서 가치를 제공할 수 있는 전문성을 확보합니다. 아래 4가지 도구 중 하나를 선택해 깊이 파고드세요.</p>
                    
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>ChatGPT & Claude (AI 교육/컨설팅 분야)</h4>
                          <p><strong>학습 내용:</strong> 단순 대화를 넘어, 기업의 특정 부서(마케팅, 영업 등) 업무 효율을 높이는 고급 프롬프트 엔지니어링, Claude의 'Skills' 기능을 활용한 워크플로우 자동화 방법을 학습합니다.</p>
                          <p><strong>주요 학습 포인트:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li>고급 프롬프트 엔지니어링 기법</li>
                            <li>Claude Skills 기능을 활용한 워크플로우 자동화</li>
                            <li>산업별 맞춤형 솔루션 개발</li>
                          </ul>
                          <p><strong>실행 과제:</strong> 특정 산업(예: 부동산, 법률)을 위한 'ChatGPT 활용 업무 자동화 템플릿 10선'을 직접 제작해 보세요.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Lovable (노코드 AI 앱 개발 분야)</h4>
                          <p><strong>학습 내용:</strong> 코딩 없이 웹사이트, 내부 관리 도구, AI 챗봇 등 간단한 소프트웨어를 구축하는 방법을 익힙니다.</p>
                          <p><strong>주요 학습 포인트:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li>노코드 플랫폼 기초 및 고급 기능</li>
                            <li>AI 챗봇 통합 및 커스터마이징</li>
                            <li>데이터베이스 연동 및 관리 시스템 구축</li>
                          </ul>
                          <p><strong>실행 과제:</strong> 주변 소상공인의 웹사이트를 Lovable로 리뉴얼해주거나, 간단한 재고 관리 툴을 만들어주며 포트폴리오를 쌓으세요.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">3</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Retell AI (AI 음성 에이전트 개발 분야)</h4>
                          <p><strong>학습 내용:</strong> AI가 전화를 받고 예약을 잡거나, 리드에게 아웃바운드 콜을 하는 음성 에이전트를 만드는 방법을 학습합니다.</p>
                          <p><strong>주요 학습 포인트:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li>음성 에이전트 설계 및 프롬프트 작성</li>
                            <li>전화 시스템 연동 및 자동화</li>
                            <li>예약 시스템 및 CRM 연동</li>
                          </ul>
                          <p><strong>실행 과제:</strong> 병원이나 레스토랑을 위한 '24시간 AI 예약 접수 상담원' 프로토타입을 구축해 보세요.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">4</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Make.com (AI 자동화 개발 분야)</h4>
                          <p><strong>학습 내용:</strong> 여러 앱과 서비스를 API로 연결하여 반복 업무를 자동화하는 시스템(예: 고객 문의 접수 시 슬랙 알림 및 CRM 자동 등록) 구축법을 익힙니다.</p>
                          <p><strong>주요 학습 포인트:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li>API 연동 및 워크플로우 설계</li>
                            <li>조건부 로직 및 에러 핸들링</li>
                            <li>데이터 변환 및 매핑</li>
                          </ul>
                          <p><strong>실행 과제:</strong> 자신의 업무 중 가장 반복적인 프로세스를 찾아 Make.com으로 자동화하는 시스템을 3개 이상 만들어 보세요.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Phase 2: 판매 단계 구축 상세 */}
                  <div className="guide-section">
                    <h3>Phase 2: 판매 단계 구축 상세</h3>
                    <p><strong>목표:</strong> 학습한 기술을 서비스로 만들어 잠재 고객에게 효과적으로 알리고 판매합니다.</p>
                    
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Blot AI / Poppy AI (콘텐츠 생성)</h4>
                          <p><strong>활용법:</strong> 성공적인 경쟁사 콘텐츠(유튜브, 블로그)를 분석하여 나만의 관점과 사례를 담은 전문적인 콘텐츠를 빠르게 대량 생산합니다. 이를 통해 전문성을 증명하고 잠재 고객을 유치합니다.</p>
                          <p><strong>주요 활용 전략:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li>경쟁사 콘텐츠 분석 및 차별화 포인트 도출</li>
                            <li>자신의 프로젝트 사례를 활용한 콘텐츠 제작</li>
                            <li>SEO 최적화 및 키워드 전략</li>
                            <li>소셜 미디어 배포 및 커뮤니티 참여</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Instantly AI (콜드 이메일 아웃리치)</h4>
                          <p><strong>활용법:</strong> 잠재 고객 리스트를 확보한 후, 개인화된 이메일을 대량으로 발송합니다. AI 자동 응답 기능을 설정하여 미팅 예약까지의 과정을 자동화하여 영업 효율을 극대화합니다.</p>
                          <p><strong>주요 활용 전략:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li>타겟 고객 리스트 구축 및 세그멘테이션</li>
                            <li>개인화된 이메일 템플릿 작성</li>
                            <li>이메일 발송 스케줄링 및 A/B 테스트</li>
                            <li>자동 응답 및 미팅 예약 시스템 구축</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">3</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Gamma (제안서 제작)</h4>
                          <p><strong>활용법:</strong> 고객과의 미팅 녹취록을 입력하면 AI가 맞춤형 제안서 초안을 수십 분 내로 생성합니다. 제안서 제작 시간을 획기적으로 단축하여 더 많은 고객에게 제안할 기회를 확보합니다.</p>
                          <p><strong>주요 활용 전략:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li>미팅 녹취록 또는 노트를 구조화된 데이터로 변환</li>
                            <li>고객별 맞춤형 제안서 템플릿 활용</li>
                            <li>가격 책정 및 패키지 구성 자동화</li>
                            <li>제안서 검토 및 수정 프로세스 최적화</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Phase 3: 확장 단계 구축 상세 */}
                  <div className="guide-section">
                    <h3>Phase 3: 확장 단계 구축 상세</h3>
                    <p><strong>목표:</strong> 에이전시 운영을 통해 축적된 경험과 노하우를 교육 상품(코스, 커뮤니티)으로 만들어 더 큰 규모의 수익을 창출합니다.</p>
                    
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Skool + Fiducia (커뮤니티 구축 및 관리)</h4>
                          <p><strong>활용법:</strong> 무료 Skool 커뮤니티를 개설하여 잠재 고객에게 가치를 제공하고 신뢰를 쌓습니다. Fiducia의 AI 관리 기능을 활용해 적은 리소스로 커뮤니티를 운영하며, 유료 교육 상품이나 에이전시 서비스로 전환시킵니다.</p>
                          <p><strong>주요 활용 전략:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li>무료 커뮤니티를 통한 가치 제공 및 신뢰 구축</li>
                            <li>Fiducia AI를 활용한 커뮤니티 관리 자동화</li>
                            <li>커뮤니티 내 질문 답변 및 멤버 관리</li>
                            <li>유료 교육 상품 또는 에이전시 서비스로 전환</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>HeyGen (교육 콘텐츠 제작)</h4>
                          <p><strong>활용법:</strong> 자신의 AI 아바타를 생성하여 교육 영상 콘텐츠를 효율적으로 제작하고 업데이트합니다. 장소나 시간에 구애받지 않고 일관된 퀄리티의 강의 영상을 만들 수 있어 콘텐츠 확장에 용이합니다.</p>
                          <p><strong>주요 활용 전략:</strong></p>
                          <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                            <li>AI 아바타 생성 및 커스터마이징</li>
                            <li>스크립트 기반 교육 영상 제작</li>
                            <li>다국어 콘텐츠 제작 및 현지화</li>
                            <li>콘텐츠 업데이트 및 버전 관리</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 주의사항 및 팁 */}
                  <div className="guide-section guide-warnings">
                    <h3>주의사항 및 팁</h3>
                    <div className="guide-tips">
                      <h4>1. 단계별 집중</h4>
                      <p>각 Phase를 순차적으로 완료하는 것이 중요합니다. Phase 1에서 충분한 전문성을 쌓지 않고 Phase 2로 넘어가면 서비스 품질이 떨어질 수 있습니다.</p>
                      
                      <h4 style={{ marginTop: '15px', fontSize: '1rem' }}>권장 타임라인:</h4>
                      <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                        <li>Phase 1: 4-8주 (도구 선택 및 전문성 확보)</li>
                        <li>Phase 2: 2-4주 (첫 고객 확보 및 서비스 판매)</li>
                        <li>Phase 3: 지속적 (교육 상품 개발 및 스케일업)</li>
                      </ul>
                      
                      <h4>2. 도구 선택의 중요성</h4>
                      <p>Phase 1에서 자신의 관심사와 시장 수요를 고려하여 적합한 도구를 선택하세요. 모든 도구를 배우려 하기보다 하나를 깊이 파는 것이 더 효과적입니다.</p>
                      
                      <h4 style={{ marginTop: '15px', fontSize: '1rem' }}>도구 선택 기준:</h4>
                      <ul className="tips-list" style={{ paddingLeft: '20px' }}>
                        <li>자신의 배경과 경험에 맞는 도구 선택</li>
                        <li>시장 수요가 높은 분야 우선 고려</li>
                        <li>학습 곡선과 시간 투자 대비 효과 고려</li>
                      </ul>
                      
                      <h4>3. 실행 과제 완수</h4>
                      <p>각 Phase의 실행 과제를 반드시 완료하세요. 이론만으로는 전문성이 쌓이지 않습니다. 실제 프로젝트를 통해 포트폴리오를 구축하고 경험을 쌓아야 합니다.</p>
                      
                      <h4>4. 지속적인 학습</h4>
                      <p>AI 도구는 빠르게 발전합니다. Phase 3에 도달한 후에도 새로운 도구와 트렌드를 지속적으로 학습하여 경쟁력을 유지하세요.</p>
                      
                      <h4>5. 커뮤니티 활용</h4>
                      <p>각 도구의 공식 커뮤니티와 사용자 그룹에 참여하여 다른 사용자들의 사례를 학습하고 네트워킹을 통해 기회를 확장하세요.</p>
                    </div>
                  </div>

                  {/* 기존 비즈니스에 즉시 적용 가능한 AI 활용 전략 */}
                  <div className="guide-section">
                    <h3>📈 기존 비즈니스에 즉시 적용 가능한 AI 활용 전략</h3>
                    
                    <div className="guide-steps" style={{ marginTop: '20px' }}>
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>B2B 지식 상품화로 신규 수익원 창출</h4>
                          <p><strong>아이디어:</strong> 현재 비즈니스에서 가장 전문성 있는 분야의 내부 노하우(예: 효율적인 프로젝트 관리법, 고객 데이터 분석법)를 AI 도구를 활용해 체계화합니다. 'HeyGen'으로 교육 영상을 만들고 'Skool'에 올려 동종 업계 다른 기업들을 위한 유료 교육 프로그램을 론칭하여 새로운 B2B 수익 모델을 구축할 수 있습니다.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>AI 기반의 '초개인화' 고객 온보딩 프로세스 구축</h4>
                          <p><strong>아이디어:</strong> 신규 고객과의 미팅 내용을 'Gamma'에 입력하여 고객의 니즈가 완벽히 반영된 맞춤형 온보딩 가이드와 제안서를 즉시 생성합니다. 또한 고객 특성별로 다른 내용을 담은 'HeyGen' 아바타 영상을 제작하여 이메일로 발송함으로써 고객 만족도와 리텐션을 극대화할 수 있습니다.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">3</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>영업 및 마케팅 파이프라인 완전 자동화</h4>
                          <p><strong>아이디어:</strong> 'Blot AI'로 타겟 고객이 관심을 가질 만한 콘텐츠를 소셜 미디어에 꾸준히 발행하여 리드를 유치합니다. 'Instantly AI'를 통해 잠재 고객에게 자동으로 접근하고, 응답이 오면 'Retell AI' 음성봇이 1차 상담을 진행하여 미팅을 잡습니다. 이 과정을 'Make.com'으로 연결하여 리드 생성부터 미팅 예약까지의 전 과정을 자동화하여 영업 생산성을 폭발적으로 증가시킬 수 있습니다.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">4</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>AI를 활용한 신규 서비스/상품 개발</h4>
                          <p><strong>아이디어:</strong> 기존 서비스에 AI 기능을 접목하여 고부가가치 상품을 출시합니다. 예를 들어, 컨설팅 회사는 'ChatGPT' API와 'Lovable'을 결합하여 고객사 전용 'AI 업무 분석 툴'을 개발해 제공할 수 있습니다. 마케팅 에이전시는 'Retell AI'를 활용한 '24시간 AI 세일즈 상담원' 서비스를 새로운 상품으로 출시하여 경쟁사와 차별화할 수 있습니다.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : id === '10' && isAuthenticated === true ? (
                <div className="guide-content">
                  {/* 시스템 개요 */}
                  <div className="guide-section guide-overview">
                    <h3>시스템 개요</h3>
                    <p>Notion의 빈 페이지에서 시작하여, PARA tagging 시스템, 자동화 템플릿, 웹 클리핑 기능까지 갖춘 포괄적인 개인용 노트 관리 시스템을 구축하는 방법을 단계별로 안내합니다.</p>
                    <p style={{ marginTop: '15px' }}>이 가이드를 따라 Notion에서 자신만의 완벽한 노트 시스템을 구축해 보세요.</p>
                  </div>

                  {/* 1단계: 기본 데이터베이스 생성 */}
                  <div className="guide-section">
                    <h3>1단계: 기본 데이터베이스 생성</h3>
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>'Ultimate Notes' 페이지 생성</h4>
                          <p>'Ultimate Notes'라는 이름의 새 페이지를 생성하고 전체 너비로 설정합니다.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>'Databases' 하위 페이지 생성</h4>
                          <p>'Ultimate Notes' 페이지 안에 'Databases'라는 하위 페이지를 만듭니다.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">3</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>데이터베이스 생성</h4>
                          <p>'Databases' 페이지에 'Notes'와 'Tags'라는 두 개의 인라인 데이터베이스를 생성합니다.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 2단계: Notes 데이터베이스 속성 설정 */}
                  <div className="guide-section">
                    <h3>2단계: 'Notes' 데이터베이스 속성 설정</h3>
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>속성 추가</h4>
                          <p>다음과 같은 속성을 추가합니다:</p>
                          <ul className="tips-list" style={{ paddingLeft: '20px', marginTop: '10px' }}>
                            <li><strong>Favorite:</strong> 체크박스</li>
                            <li><strong>Tags:</strong> 'Tags' 데이터베이스와 '관계형'으로 연결</li>
                            <li><strong>Type:</strong> '선택' 속성 (옵션: Voice Note, Web Clip, Meeting, Journal)</li>
                            <li><strong>URL:</strong> URL</li>
                            <li><strong>Note Date:</strong> 날짜</li>
                            <li><strong>Archived:</strong> 체크박스</li>
                            <li><strong>Updated:</strong> 최종 편집 일시</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>페이지 레이아웃 사용자 정의</h4>
                          <p>페이지 레이아웃을 사용자 정의하여 <code>Tags</code>, <code>Favorite</code>, <code>Type</code>, <code>URL</code>을 상단에 고정하고 나머지는 사이드 패널로 이동시켜 깔끔하게 정리합니다.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 3단계: Tags 데이터베이스 속성 설정 */}
                  <div className="guide-section">
                    <h3>3단계: 'Tags' 데이터베이스 속성 설정</h3>
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>속성 추가</h4>
                          <p>다음과 같은 속성을 추가합니다:</p>
                          <ul className="tips-list" style={{ paddingLeft: '20px', marginTop: '10px' }}>
                            <li><strong>Type:</strong> '상태' 속성 (옵션: Area, Resource, Entity - PARA 메소드 기반)</li>
                            <li><strong>Parent Tag:</strong> 'Tags' 데이터베이스 자체와 '관계형'으로 연결</li>
                            <li><strong>Subtags:</strong> 'Tags' 데이터베이스 자체와 '관계형'으로 연결하여 계층 구조 생성</li>
                            <li><strong>Archived:</strong> 체크박스</li>
                            <li><strong>Note Count:</strong> 포뮬러 (관련된 노트 개수 계산)</li>
                            <li><strong>Latest Activity:</strong> 포뮬러 (가장 최근 노트 활동 시간 추적)</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>페이지 레이아웃 사용자 정의</h4>
                          <p>페이지 레이아웃을 사용자 정의하여 핵심 정보만 보이도록 정리합니다.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 4단계: 대시보드 뷰 구성 */}
                  <div className="guide-section">
                    <h3>4단계: 대시보드 뷰 구성</h3>
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>'Notes' 데이터베이스 뷰 생성</h4>
                          <p>'Ultimate Notes' 메인 페이지로 돌아와 'Notes' 데이터베이스의 '연결된 데이터베이스 보기'를 생성합니다.</p>
                          <p style={{ marginTop: '10px' }}>목적에 따라 다양한 뷰(탭)를 추가하고 필터를 설정합니다:</p>
                          <ul className="tips-list" style={{ paddingLeft: '20px', marginTop: '10px' }}>
                            <li><strong>All:</strong> 보관되지 않은 모든 노트 표시</li>
                            <li><strong>Inbox:</strong> 태그가 지정되지 않은 노트만 표시 (처리할 항목 관리)</li>
                            <li><strong>Clips:</strong> URL이 있는 노트(웹 클립)만 표시</li>
                            <li><strong>Journal:</strong> Type이 'Journal'인 노트만 표시</li>
                            <li><strong>Meetings:</strong> Type이 'Meetings'인 노트만 표시</li>
                            <li><strong>Favorites:</strong> 'Favorite' 체크박스가 선택된 노트만 표시</li>
                          </ul>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>'Tags' 데이터베이스 뷰 생성</h4>
                          <p>'Tags' 데이터베이스에 대해서도 동일하게 뷰를 생성합니다:</p>
                          <ul className="tips-list" style={{ paddingLeft: '20px', marginTop: '10px' }}>
                            <li><strong>Recent:</strong> 최근 활동한 태그 표시</li>
                            <li><strong>A-Z:</strong> 알파벳 순서로 정렬</li>
                            <li><strong>Types:</strong> 타입별 그룹화 (Area, Resource, Entity)</li>
                            <li><strong>Favorites:</strong> 즐겨찾기 태그만 표시</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 5단계: 자동화 템플릿 생성 */}
                  <div className="guide-section">
                    <h3>5단계: 자동화 템플릿 생성</h3>
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Tags 템플릿 생성</h4>
                          <p>'Tags' 데이터베이스에서 새 템플릿을 만듭니다. 페이지 내에 해당 태그와 관련된 노트 및 웹 클립을 자동으로 필터링하여 보여주는 '연결된 데이터베이스 보기'를 추가합니다. (Self-referencing filter 활용)</p>
                          <p style={{ marginTop: '10px' }}>이 템플릿을 기본값으로 설정합니다.</p>
                        </div>
                      </div>

                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content guide-node-detail">
                          <h4>Journal/Meeting 템플릿 생성</h4>
                          <p>'Notes' 데이터베이스에서 저널 및 미팅용 템플릿을 만듭니다.</p>
                          <ul className="tips-list" style={{ paddingLeft: '20px', marginTop: '10px' }}>
                            <li>제목에 <code>@오늘</code>을 넣어 날짜를 자동 입력</li>
                            <li>미리 정의된 섹션 추가:
                              <ul style={{ paddingLeft: '20px', marginTop: '5px' }}>
                                <li>Journal 템플릿: Day Plan, Reflections, Action Items</li>
                                <li>Meeting 템플릿: Agenda, Notes, Action Items, Follow-ups</li>
                              </ul>
                            </li>
                            <li>각 템플릿을 해당 뷰의 기본값으로 설정</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 주의사항 및 팁 */}
                  <div className="guide-section guide-warnings">
                    <h3>주의사항 및 팁</h3>
                    <div className="guide-tips">
                      <h4>1. PARA 메소드 이해</h4>
                      <p>PARA 메소드는 Projects, Areas, Resources, Archives의 약자로, 정보를 효율적으로 분류하는 시스템입니다. Tags의 Type 속성에서 Area, Resource, Entity를 사용하여 정보를 체계적으로 관리할 수 있습니다.</p>
                      
                      <h4>2. 관계형 속성 활용</h4>
                      <p>Tags 데이터베이스에서 Parent Tag와 Subtags를 관계형으로 연결하면 계층적 태그 구조를 만들 수 있습니다. 이를 통해 더 유연하고 확장 가능한 태그 시스템을 구축할 수 있습니다.</p>
                      
                      <h4>3. 템플릿 활용</h4>
                      <p>자주 사용하는 노트 형식은 템플릿으로 만들어두면 시간을 절약할 수 있습니다. 특히 Journal과 Meeting 템플릿은 매일 사용하는 경우가 많으므로 미리 구성해두는 것이 좋습니다.</p>
                      
                      <h4>4. 뷰 필터 최적화</h4>
                      <p>각 뷰의 필터를 목적에 맞게 설정하면 필요한 정보를 빠르게 찾을 수 있습니다. Inbox 뷰는 처리할 항목을 관리하는 데 특히 유용합니다.</p>
                      
                      <h4>5. 정기적인 정리</h4>
                      <p>Archived 속성을 활용하여 더 이상 필요하지 않은 노트나 태그를 보관 처리하세요. 정기적으로 정리하면 시스템이 더 효율적으로 작동합니다.</p>
                    </div>
                  </div>

                  {/* AI 검색 영역 */}
                  <div className="ai-search-section">
                    <div className="ai-search-header">
                      <h3 className="ai-search-title">AI 검색</h3>
                      <p className="ai-search-description">
                        워크플로우 개발에 대한 궁금한 점을 검색해보세요. RAG 기반 AI가 관련 정보를 제공합니다.
                      </p>
                      {isAuthenticated === true && (
                        <p className="ai-search-limit-info">
                          하루 {MAX_SEARCHES_PER_DAY}회 제한 (오늘 {searchCount}/{MAX_SEARCHES_PER_DAY}회 사용)
                        </p>
                      )}
                    </div>
                    
                    <div className="ai-search-container">
                      {isAuthenticated !== true ? (
                        <div className="ai-search-login-required">
                          <p className="ai-search-login-message">
                            <Icon icon="mdi:lock-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> 로그인이 필요합니다. 로그인 후 AI 검색을 사용할 수 있습니다.
                          </p>
                          <button 
                            className="ai-search-login-btn"
                            onClick={() => signInWithRedirect()}
                          >
                            로그인하기
                          </button>
                        </div>
                      ) : (
                        <>
                          <form onSubmit={handleSearchSubmit} className="ai-search-form">
                            <div className="ai-search-input-wrapper">
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="워크플로우 개발에 대한 궁금한 점을 검색하세요..."
                                className="ai-search-input"
                                disabled={isSearching || searchLimitReached}
                              />
                              <button 
                                type="submit" 
                                className="ai-search-btn"
                                disabled={!searchQuery.trim() || isSearching || searchLimitReached}
                              >
                                {isSearching ? <Icon icon="mdi:loading" width={18} height={18} className="spin-icon" /> : <Icon icon="mdi:magnify" width={18} height={18} />}
                              </button>
                            </div>
                          </form>
                          
                          {/* 검색 결과 표시 */}
                          {isSearching && (
                            <div className="ai-search-loading">
                              <div className="loading-spinner"></div>
                              <p>AI가 검색 중입니다...</p>
                            </div>
                          )}
                          
                          {searchError && (
                            <div className="ai-search-error">
                              <p><Icon icon="mdi:close-circle-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />{searchError}</p>
                            </div>
                          )}
                          
                          {searchLimitReached && !isSearching && (
                            <div className="ai-search-limit-message">
                              <p><Icon icon="mdi:alert-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />하루 최대 {MAX_SEARCHES_PER_DAY}회 검색 제한에 도달했습니다. 내일 다시 시도해주세요.</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : id !== '9' && id !== '10' && isAuthenticated === true ? (
                <div className="guide-content">
                  <div className="guide-section">
                    <h3>개발 환경 설정</h3>
                    <div className="guide-steps">
                      <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content">
                          <h4>필요한 도구 설치</h4>
                          <p>n8n 및 Node.js가 설치되어 있어야 합니다. 최신 버전을 권장합니다.</p>
                          <div className="guide-code">
                            <code>npm install -g n8n</code>
                          </div>
                        </div>
                      </div>
                      <div className="guide-step">
                        <div className="guide-step-number">2</div>
                        <div className="guide-step-content">
                          <h4>워크플로우 파일 가져오기</h4>
                          <p>다운로드한 워크플로우 소스 파일을 n8n에 import 합니다.</p>
                          <div className="guide-code">
                            <code>n8n import:workflow --file=workflow.json</code>
                          </div>
                        </div>
                      </div>
                      <div className="guide-step">
                        <div className="guide-step-number">3</div>
                        <div className="guide-step-content">
                          <h4>환경 변수 설정</h4>
                          <p>필요한 API 키 및 환경 변수를 설정합니다.</p>
                          <div className="guide-code">
                            <code>export API_KEY=your_api_key</code>
                          </div>
                        </div>
                      </div>
                      <div className="guide-step">
                        <div className="guide-step-number">4</div>
                        <div className="guide-step-content">
                          <h4>워크플로우 활성화</h4>
                          <p>n8n 대시보드에서 워크플로우를 활성화하고 실행합니다.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="guide-tips">
                    <h3><Icon icon="mdi:lightbulb-outline" width={20} height={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />개발 팁</h3>
                    <ul className="tips-list">
                      <li>워크플로우를 수정하기 전에 반드시 백업을 만드세요.</li>
                      <li>각 노드의 설정을 확인하고 필요한 인증 정보를 입력하세요.</li>
                      <li>테스트 모드로 실행하여 오류를 확인한 후 실제 환경에 배포하세요.</li>
                      <li>로그를 확인하여 워크플로우 실행 상태를 모니터링하세요.</li>
                    </ul>
                  </div>
                  
                  <div className="guide-resources">
                    <h3><Icon icon="mdi:book-open-outline" width={20} height={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />참고 자료</h3>
                    <div className="resources-list">
                      <div className="resource-item">
                        <span className="resource-icon"><Icon icon="mdi:file-document-outline" width={22} height={22} /></span>
                        <div>
                          <h4>n8n 공식 문서</h4>
                          <p>n8n의 공식 문서에서 더 자세한 정보를 확인할 수 있습니다.</p>
                        </div>
                      </div>
                      <div className="resource-item">
                        <span className="resource-icon"><Icon icon="mdi:forum-outline" width={22} height={22} /></span>
                        <div>
                          <h4>커뮤니티 포럼</h4>
                          <p>다른 개발자들과 경험을 공유하고 질문할 수 있습니다.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AI 검색 영역 */}
                  <div className="ai-search-section">
                    <div className="ai-search-header">
                      <h3 className="ai-search-title">AI 검색</h3>
                      <p className="ai-search-description">
                        워크플로우 개발에 대한 궁금한 점을 검색해보세요. RAG 기반 AI가 관련 정보를 제공합니다.
                      </p>
                      {isAuthenticated === true && (
                        <p className="ai-search-limit-info">
                          하루 {MAX_SEARCHES_PER_DAY}회 제한 (오늘 {searchCount}/{MAX_SEARCHES_PER_DAY}회 사용)
                        </p>
                      )}
                    </div>
                    
                    <div className="ai-search-container">
                      {isAuthenticated !== true ? (
                        <div className="ai-search-login-required">
                          <p className="ai-search-login-message">
                            <Icon icon="mdi:lock-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> 로그인이 필요합니다. 로그인 후 AI 검색을 사용할 수 있습니다.
                          </p>
                          <button 
                            className="ai-search-login-btn"
                            onClick={() => signInWithRedirect()}
                          >
                            로그인하기
                          </button>
                        </div>
                      ) : (
                        <>
                          <form onSubmit={handleSearchSubmit} className="ai-search-form">
                            <div className="ai-search-input-wrapper">
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="워크플로우 개발에 대한 궁금한 점을 검색하세요..."
                                className="ai-search-input"
                                disabled={isSearching || searchLimitReached}
                              />
                              <button 
                                type="submit" 
                                className="ai-search-btn"
                                disabled={!searchQuery.trim() || isSearching || searchLimitReached}
                              >
                                {isSearching ? <Icon icon="mdi:loading" width={18} height={18} className="spin-icon" /> : <Icon icon="mdi:magnify" width={18} height={18} />}
                              </button>
                            </div>
                          </form>
                          
                          {/* 검색 결과 표시 */}
                          {isSearching && (
                            <div className="ai-search-loading">
                              <div className="loading-spinner"></div>
                              <p>AI가 검색 중입니다...</p>
                            </div>
                          )}
                          
                          {searchError && (
                            <div className="ai-search-error">
                              <p><Icon icon="mdi:close-circle-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />{searchError}</p>
                            </div>
                          )}
                          
                          {searchLimitReached && !isSearching && (
                            <div className="ai-search-limit-message">
                              <p><Icon icon="mdi:alert-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />하루 최대 {MAX_SEARCHES_PER_DAY}회 검색 제한에 도달했습니다. 내일 다시 시도해주세요.</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* AI 검색 영역 */}
                  <div className="ai-search-section">
                    <div className="ai-search-header">
                      <h3 className="ai-search-title">AI 검색</h3>
                      <p className="ai-search-description">
                        워크플로우 개발에 대한 궁금한 점을 검색해보세요. RAG 기반 AI가 관련 정보를 제공합니다.
                      </p>
                      {isAuthenticated === true && (
                        <p className="ai-search-limit-info">
                          하루 {MAX_SEARCHES_PER_DAY}회 제한 (오늘 {searchCount}/{MAX_SEARCHES_PER_DAY}회 사용)
                        </p>
                      )}
                    </div>
                    
                    <div className="ai-search-container">
                      {isAuthenticated !== true ? (
                        <div className="ai-search-login-required">
                          <p className="ai-search-login-message">
                            <Icon icon="mdi:lock-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> 로그인이 필요합니다. 로그인 후 AI 검색을 사용할 수 있습니다.
                          </p>
                          <button 
                            className="ai-search-login-btn"
                            onClick={() => signInWithRedirect()}
                          >
                            로그인하기
                          </button>
                        </div>
                      ) : (
                        <>
                          <form onSubmit={handleSearchSubmit} className="ai-search-form">
                            <div className="ai-search-input-wrapper">
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="워크플로우 개발에 대한 궁금한 점을 검색하세요..."
                                className="ai-search-input"
                                disabled={isSearching || searchLimitReached}
                              />
                              <button 
                                type="submit" 
                                className="ai-search-btn"
                                disabled={!searchQuery.trim() || isSearching || searchLimitReached}
                              >
                                {isSearching ? <Icon icon="mdi:loading" width={18} height={18} className="spin-icon" /> : <Icon icon="mdi:magnify" width={18} height={18} />}
                              </button>
                            </div>
                          </form>
                          
                          {/* 검색 결과 표시 */}
                          {isSearching && (
                            <div className="ai-search-loading">
                              <div className="loading-spinner"></div>
                              <p>AI가 검색 중입니다...</p>
                            </div>
                          )}
                          
                          {searchError && (
                            <div className="ai-search-error">
                              <p><Icon icon="mdi:close-circle-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />{searchError}</p>
                            </div>
                          )}
                          
                          {searchLimitReached && !isSearching && (
                            <div className="ai-search-limit-message">
                              <p><Icon icon="mdi:alert-outline" width={16} height={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />하루 최대 {MAX_SEARCHES_PER_DAY}회 검색 제한에 도달했습니다. 내일 다시 시도해주세요.</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="process-detail-button-container">
                  <p className="process-detail-notice">
                    로그인하시면 상세한 내용을 확인할 수 있습니다.
                  </p>
                  <button 
                    className="btn-show-process-detail"
                    onClick={() => signInWithRedirect()}
                  >
                    로그인하기
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="related-workflows">
          <h3>관련 워크플로우</h3>
          <div className="related-grid">
            <div className="related-card">
              <div className="related-image" style={{ background: 'linear-gradient(135deg, #007BFF 0%, #0062CC 100%)' }}>
                <Icon icon="mdi:calculator-variant-outline" width={32} height={32} color="#ffffff" />
              </div>
              <h4>회계 자동화 및 세무 신고</h4>
              <span className="related-price">149,000원</span>
            </div>
            <div className="related-card">
              <div className="related-image" style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}>
                <Icon icon="mdi:crosshairs-gps" width={32} height={32} color="#ffffff" />
              </div>
              <h4>경쟁사 서비스 공략 포인트 분석</h4>
              <span className="related-price">199,000원</span>
            </div>
            <div className="related-card">
              <div className="related-image" style={{ background: 'linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)' }}>
                <Icon icon="mdi:bell-badge-outline" width={32} height={32} color="#ffffff" />
              </div>
              <h4>정부과제 신규 공지 자동 알람</h4>
              <span className="related-price">299,000원</span>
            </div>
          </div>
        </div>
      </div>

      {/* 이미지 확대 모달 */}
      {enlargedImage && (
        <div 
          className="image-modal-overlay" 
          onClick={() => setEnlargedImage(null)}
        >
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="image-modal-close" 
              onClick={() => setEnlargedImage(null)}
              aria-label="닫기"
            >
              ×
            </button>
            <img 
              src={enlargedImage.src} 
              alt={enlargedImage.alt}
              className="image-modal-image"
            />
          </div>
        </div>
      )}

      {/* AI 검색 결과 모달 */}
      {showSearchModal && searchResult && (
        <div 
          className="ai-search-modal-overlay" 
          onClick={handleCloseSearchModal}
        >
          <div className="ai-search-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="ai-search-modal-header">
              <h2 className="ai-search-modal-title">
                {searchResult.isError ? <><Icon icon="mdi:alert-outline" width={20} height={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />웹훅 호출 오류</> : 'AI 검색 결과'}
              </h2>
              <button 
                className="ai-search-modal-close" 
                onClick={handleCloseSearchModal}
                aria-label="닫기"
              >
                <Icon icon="mdi:close" width={20} height={20} />
              </button>
            </div>
            
            <div className="ai-search-modal-content">
              {searchResult.query && (
                <div className="ai-search-modal-query">
                  <strong>검색어:</strong> {searchResult.query}
                </div>
              )}
              
              <div className={`ai-search-modal-result ${searchResult.isError ? 'ai-search-modal-error' : ''}`}>
                <h3>{searchResult.isError ? '오류 정보' : '응답 내용'}</h3>
                <div className="ai-search-modal-answer">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {searchResult.answer}
                  </ReactMarkdown>
                </div>
              </div>
              
              {searchResult.citations && searchResult.citations.length > 0 && (
                <div className="ai-search-modal-citations">
                  <h3>참고 자료</h3>
                  <ul>
                    {searchResult.citations.map((citation, index) => (
                      <li key={index}>
                        <div className="citation-chunk">
                          {citation.chunk && citation.chunk.length > 0 && (
                            <p className="citation-text">
                              {citation.chunk.substring(0, 200)}
                              {citation.chunk.length > 200 ? '...' : ''}
                            </p>
                          )}
                          {citation.score && (
                            <span className="citation-score">
                              관련도: {(citation.score * 100).toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div className="ai-search-modal-footer">
              <button 
                className="ai-search-modal-close-btn" 
                onClick={handleCloseSearchModal}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowDetail;